/* mdvWishWorker-1 - Maniere De Voir website wishlist on the Worker signed app-proxy route (Phase T, 10 Oct 2026).
   Loaded only when shop.metafields.mdv_wishlist.transport is "worker" (snippets/cirkle-wishlist.liquid); otherwise the
   legacy assets/cirkle-wishlist.js loads instead. Never both.
   (a) vendored client: mdv-security-api website/wishlist-client.js @ ee13dbb (sha256 1f6bcc33...0711), allowed edits only:
       P1 classic-script packaging (export removed, one strict scope), E1 structuredClone, E2 Object.hasOwn and
       E3 AbortSignal.timeout fallbacks (Safari >= 15.4), E4 429 -> wishlist_rate_limited with Retry-After 1-60 s.
   (b) mdvWishT facade: serial queue, merged reads, intents, server truth after writes, no legacy fallback.
   (c) the live flat renderer (cirkle-wishlist.js mdvWishGrid-1/1a + mdvWishFlat-1 + mdvWishIds-1, edc8818) with its
       wishlist reads/writes routed through mdvWishT, F-C step 2 class selectors, and the F-B do-not-port code removed.
   Same globals as cirkle-wishlist.js: wishlistBtnPDP, CSRetrieveWishlistProData, CSRetrieveWishlistData,
   CSWishlistrefreshData, ajaxcustomer_id (a UI hint only; never sent). */
var MdvWishClient = (function () { // mdvWishWorker-1 packaging (P1): the client in one classic-script scope
'use strict'; // mdvWishWorker-1 packaging (P1): module code is strict
const hashPattern = /^[a-f0-9]{64}$/;
const idPattern = /^[1-9][0-9]{0,19}$/;
const clone = value => (value === undefined ? undefined : typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value))); // mdvWishWorker-1 edit (E1): structuredClone fallback
const same = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const hasOwn = (target, field) => (typeof Object.hasOwn === 'function' ? Object.hasOwn(target, field) : Object.prototype.hasOwnProperty.call(target, field)); // mdvWishWorker-1 edit (E2): Object.hasOwn fallback
const timeoutSignal = ms => { if (typeof AbortSignal.timeout === 'function') return AbortSignal.timeout(ms); const controller = new AbortController(); setTimeout(() => controller.abort(), ms); return controller.signal; }; // mdvWishWorker-1 edit (E3): AbortSignal.timeout fallback
const exact = (value, fields) => object(value) && Object.keys(value).length === fields.length && fields.every(field => hasOwn(value, field)); // mdvWishWorker-1 edit (E2)
const hash = value => typeof value === 'string' && hashPattern.test(value);
const id = value => typeof value === 'string' && idPattern.test(value);

class WishlistFailure extends Error { // mdvWishWorker-1 packaging (P1): export removed, classic script
  constructor(code) { super(code); this.name = 'WishlistFailure'; this.code = code; }
}
const requireValue = (value, code = 'wishlist_response_unavailable') => { if (!value) throw new WishlistFailure(code); };

function actionRecord(value) {
  const fields = {
    'category.create': ['type', 'listId', 'name'], 'category.rename': ['type', 'categoryId', 'name'],
    'category.delete': ['type', 'categoryId'], 'product.remove': ['type', 'categoryId', 'productId'],
    'product.add': ['version', 'type', 'categoryId', 'shopifyProductId', 'shopifyVariantId', 'country'],
    'product.move': ['version', 'type', 'categoryId', 'sourceCategoryId', 'shopifyProductId', 'shopifyVariantId', 'country'],
  }[value?.type];
  requireValue(fields && exact(value, fields), 'wishlist_action_invalid');
  const result = {};
  for (const field of fields) {
    let item = value[field];
    if (field === 'version') requireValue(item === 2, 'wishlist_action_invalid');
    else if (field === 'name') {
      requireValue(typeof item === 'string' && [...item].length <= 255 && !/[\x00-\x1f\x7f]|[\uD800-\uDFFF]/u.test(item), 'wishlist_action_invalid');
      item = item.replace(/^ +| +$/g, ''); requireValue(item.length > 0, 'wishlist_action_invalid');
    } else if (field === 'country') requireValue(typeof item === 'string' && /^[A-Z]{2}$/.test(item), 'wishlist_action_invalid');
    else if (field.startsWith('shopify')) {
      const type = field === 'shopifyProductId' ? 'Product' : 'ProductVariant';
      const match = typeof item === 'string' && new RegExp(`^gid://shopify/${type}/([1-9][0-9]{0,18})$`).exec(item);
      requireValue(match && BigInt(match[1]) <= 9223372036854775807n, 'wishlist_action_invalid');
    } else if (field.endsWith('Id')) requireValue(field === 'listId' && item === null || id(item), 'wishlist_action_invalid');
    result[field] = item;
  }
  return result;
}

function envelopeRecord(value) {
  requireValue(exact(value, ['ticket', 'snapshotHash', 'action']) &&
    exact(value.ticket, ['revision', 'nonce']) && Number.isSafeInteger(value.ticket.revision) && value.ticket.revision > 0 &&
    hash(value.ticket.nonce) && hash(value.snapshotHash), 'wishlist_recovery_invalid');
  return { ticket: { revision: value.ticket.revision, nonce: value.ticket.nonce }, snapshotHash: value.snapshotHash, action: actionRecord(value.action) };
}

function pendingRecord(value, partition) {
  requireValue(exact(value, ['version', 'partition', 'contextHash', 'envelope']) && value.version === 1 &&
    hash(value.partition) && value.partition === partition && hash(value.contextHash), 'wishlist_recovery_invalid');
  return { version: 1, partition, contextHash: value.contextHash, envelope: envelopeRecord(value.envelope) };
}

class WishlistJournal { // mdvWishWorker-1 packaging (P1): export removed, classic script
  constructor(indexedDB = globalThis.indexedDB) { this.indexedDB = indexedDB; this.connection = null; }

  async open() {
    if (!this.connection) this.connection = new Promise((resolve, reject) => {
      let request, stopped = false;
      const fail = () => { stopped = true; reject(new WishlistFailure('wishlist_storage_unavailable')); };
      try { request = this.indexedDB.open('mdv-wishlist-recovery-v1', 1); } catch { fail(); return; }
      request.onupgradeneeded = () => {
        if (stopped) { request.transaction.abort(); return; }
        request.result.createObjectStore('pending', { keyPath: 'partition' });
      };
      request.onblocked = fail; request.onerror = fail;
      request.onsuccess = () => {
        const database = request.result;
        if (stopped) { database.close(); return; }
        database.onversionchange = () => { database.close(); this.connection = null; };
        database.onclose = () => { this.connection = null; };
        resolve(database);
      };
    });
    try { return await this.connection; } catch (error) { this.connection = null; throw error; }
  }

  async transaction(partition, mode, change) {
    requireValue(hash(partition), 'wishlist_recovery_invalid');
    const database = await this.open();
    return new Promise((resolve, reject) => {
      let transaction, result, failure;
      try {
        transaction = database.transaction('pending', mode, { durability: 'strict' });
        if (mode === 'readwrite') requireValue(transaction.durability === 'strict', 'wishlist_storage_unavailable');
        const store = transaction.objectStore('pending'), request = store.get(partition);
        request.onsuccess = () => {
          try { result = change(store, request.result === undefined ? null : pendingRecord(request.result, partition)); }
          catch (error) { failure = error; transaction.abort(); }
        };
        transaction.oncomplete = () => resolve(clone(result));
        transaction.onabort = () => reject(failure ?? new WishlistFailure('wishlist_storage_unavailable'));
        transaction.onerror = () => { failure ??= new WishlistFailure('wishlist_storage_unavailable'); };
      } catch (error) {
        try { transaction?.abort(); } catch {}
        reject(error instanceof WishlistFailure ? error : new WishlistFailure('wishlist_storage_unavailable'));
      }
    });
  }

  read(partition) { return this.transaction(partition, 'readonly', (_store, current) => current); }
  claim(record) {
    const next = pendingRecord(record, record.partition);
    return this.transaction(next.partition, 'readwrite', (store, current) => {
      requireValue(current === null, 'wishlist_operation_pending');
      store.add(next); return next;
    });
  }
  settle(record) {
    const expected = pendingRecord(record, record.partition);
    return this.transaction(expected.partition, 'readwrite', (store, current) => {
      if (current === null) return false;
      requireValue(same(current, expected), 'wishlist_operation_pending');
      store.delete(expected.partition); return true;
    });
  }
  async close() { if (this.connection) (await this.connection).close(); this.connection = null; }
}

function bootstrapRecord(value) {
  requireValue(exact(value, ['schemaVersion', 'accountPartition', 'contextHash', 'csrf', 'expiresAt']) && value.schemaVersion === 1 &&
    hash(value.accountPartition) && hash(value.contextHash) && Number.isSafeInteger(value.expiresAt) &&
    typeof value.csrf === 'string' && /^mdvwc1\.[1-9][0-9]{0,11}\.[a-f0-9]{64}\.[a-f0-9]{64}$/.test(value.csrf));
  return value;
}

function snapshotRecord(value) {
  requireValue(exact(value, ['schemaVersion', 'snapshotHash', 'wishlist', 'ticket']) && value.schemaVersion === 1 &&
    hash(value.snapshotHash) && exact(value.ticket, ['revision', 'nonce']) && Number.isSafeInteger(value.ticket.revision) &&
    value.ticket.revision > 0 && hash(value.ticket.nonce) && exact(value.wishlist, ['lists', 'categories', 'memberships']));
  for (const key of ['lists', 'categories', 'memberships']) requireValue(Array.isArray(value.wishlist[key]) && value.wishlist[key].length <= 5000);
  const { lists, categories, memberships } = value.wishlist;
  requireValue(lists.every(row => exact(row, ['id']) && id(row.id)) && new Set(lists.map(row => row.id)).size === lists.length);
  requireValue(categories.every(row => exact(row, ['id', 'listId', 'name']) && id(row.id) && lists.some(list => list.id === row.listId) &&
    (row.name === null || typeof row.name === 'string' && [...row.name].length <= 255 && !/[\x00-\x1f\x7f]/.test(row.name))) &&
    new Set(categories.map(row => row.id)).size === categories.length);
  requireValue(memberships.every(row => exact(row, ['categoryId', 'productId', 'variantId', 'savedAt']) &&
    categories.some(category => category.id === row.categoryId) && id(row.productId) && (row.variantId === null || id(row.variantId)) &&
    (row.savedAt === null || typeof row.savedAt === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(row.savedAt))));
  return value;
}

function operationRecord(value) {
  requireValue(object(value) && value.schemaVersion === 1 && typeof value.refreshRequired === 'boolean' &&
    ['applied', 'rejected', 'not_dispatched', 'outcome_unavailable', 'pending', 'unrecorded'].includes(value.status) &&
    Object.keys(value).every(field => ['schemaVersion', 'status', 'refreshRequired', 'result', 'code'].includes(field)));
  return value;
}

class WishlistClient { // mdvWishWorker-1 packaging (P1): export removed, classic script
  constructor({ source, proxyPath, country, origin = globalThis.location?.origin,
    fetchImpl = globalThis.fetch.bind(globalThis), journal = new WishlistJournal(), onInvalidate = () => {} }) {
    const url = new URL(origin);
    requireValue(url.origin === origin && url.protocol === 'https:' && !url.username && !url.password && !url.port &&
      typeof source === 'string' && /^[a-z][a-z0-9_]{1,31}$/.test(source) && typeof proxyPath === 'string' &&
      /^\/[a-z][a-z0-9_-]{0,63}\/[a-z0-9][a-z0-9_-]{0,63}$/.test(proxyPath) && /^[A-Z]{2}$/.test(country), 'wishlist_configuration_unavailable');
    this.source = source; this.proxyPath = proxyPath; this.country = country; this.origin = origin;
    this.fetchImpl = fetchImpl; this.journal = journal; this.onInvalidate = onInvalidate;
    this.session = null; this.snapshot = null; this.generation = 0; this.busy = false;
  }

  invalidate() {
    this.generation++; this.session = null; this.snapshot = null;
    this.onInvalidate();
  }

  async exclusive(task) {
    requireValue(!this.busy, 'wishlist_busy'); this.busy = true;
    try { return await task(); } finally { this.busy = false; }
  }

  guard(generation) { requireValue(generation === this.generation, 'wishlist_session_changed'); }

  async send(operation, request, session, generation) {
    this.guard(generation);
    const method = operation === 'bootstrap' ? 'GET' : 'POST';
    const body = method === 'GET' ? '' : JSON.stringify({ csrf: session.csrf, request });
    const bytes = new TextEncoder().encode(body);
    requireValue(bytes.length <= 32768, 'wishlist_action_invalid');
    const digest = [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))].map(byte => byte.toString(16).padStart(2, '0')).join('');
    const query = new URLSearchParams({ mdv_source: this.source, mdv_operation: operation, mdv_method: method, mdv_body_sha256: digest });
    this.guard(generation);
    let response, value;
    try {
      response = await this.fetchImpl(`${this.origin}${this.proxyPath}/${operation}?${query}`, {
        method, credentials: 'same-origin', mode: 'same-origin', redirect: 'error', cache: 'no-store', referrerPolicy: 'no-referrer',
        headers: { accept: 'application/json', ...(method === 'POST' ? { 'content-type': 'application/json' } : {}) },
        ...(method === 'POST' ? { body } : {}), signal: timeoutSignal(20000), // mdvWishWorker-1 edit (E3)
      });
      this.guard(generation);
      if (response.status === 429) { // mdvWishWorker-1 edit (E4): rate limited, Retry-After clamped to 1-60 s (absent: 60)
        const seconds = Number.parseInt(response.headers.get('retry-after') ?? '', 10); // mdvWishWorker-1 edit (E4)
        const limited = new WishlistFailure('wishlist_rate_limited'); // mdvWishWorker-1 edit (E4)
        limited.retryAfter = Math.min(60, Math.max(1, Number.isFinite(seconds) ? seconds : 60)); // mdvWishWorker-1 edit (E4)
        await response.body?.cancel().catch(() => {}); // mdvWishWorker-1 edit (E4)
        throw limited; // mdvWishWorker-1 edit (E4)
      } // mdvWishWorker-1 edit (E4)
      requireValue(response.headers.get('content-type')?.split(';')[0].trim() === 'application/json');
      const reader = response.body.getReader(), chunks = []; let length = 0;
      try {
        while (true) {
          const chunk = await reader.read(); if (chunk.done) break;
          length += chunk.value.byteLength; requireValue(length <= 4 * 1024 * 1024); chunks.push(chunk.value);
        }
      } finally { await reader.cancel().catch(() => {}); }
      const combined = new Uint8Array(length); let offset = 0;
      for (const chunk of chunks) { combined.set(chunk, offset); offset += chunk.length; }
      value = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(combined));
    } catch (error) {
      this.guard(generation);
      throw error instanceof WishlistFailure ? error : new WishlistFailure('wishlist_response_unavailable');
    }
    this.guard(generation);
    if (!response.ok) {
      if ([401, 403].includes(response.status)) { this.invalidate(); throw new WishlistFailure('wishlist_authentication_required'); }
      const supported = new Set(['wishlist_operation_pending', 'wishlist_context_conflict', 'account_deletion_pending',
        'wishlist_snapshot_conflict', 'wishlist_revision_conflict', 'wishlist_catalog_disabled', 'wishlist_writes_disabled']);
      throw new WishlistFailure(supported.has(value?.error?.code) ? value.error.code : 'wishlist_response_unavailable');
    }
    return value;
  }

  async bootstrap() {
    const generation = this.generation;
    let next;
    try { next = bootstrapRecord(await this.send('bootstrap', {}, null, generation)); }
    catch (error) { if (generation === this.generation) this.invalidate(); throw error; }
    this.guard(generation);
    const previous = this.session;
    if (previous && (previous.accountPartition !== next.accountPartition || previous.contextHash !== next.contextHash)) this.invalidate();
    this.session = next;
    return next;
  }

  async readSnapshot(session, generation) {
    const result = snapshotRecord(await this.send('snapshot', {}, session, generation));
    this.guard(generation); return result;
  }

  async recover(session, generation, record) {
    requireValue(record.contextHash === session.contextHash, 'wishlist_context_conflict');
    let outcome = operationRecord(await this.send('status', record.envelope, session, generation));
    if (outcome.status === 'pending') outcome = operationRecord(await this.send('resolve', record.envelope, session, generation));
    requireValue(outcome.status !== 'pending', 'wishlist_operation_pending');
    const snapshot = await this.readSnapshot(session, generation);
    this.guard(generation);
    await this.journal.settle(record);
    this.guard(generation);
    return { snapshot, outcome: outcome.status };
  }

  refresh() {
    return this.exclusive(async () => {
      const session = await this.bootstrap(), generation = this.generation;
      const record = await this.journal.read(session.accountPartition); this.guard(generation);
      const result = record ? await this.recover(session, generation, record) : { snapshot: await this.readSnapshot(session, generation), outcome: null };
      this.guard(generation); this.snapshot = result.snapshot;
      return clone(result);
    });
  }

  mutate(action) {
    const safeAction = actionRecord(action);
    return this.exclusive(async () => {
      const previous = this.session, snapshot = this.snapshot;
      requireValue(previous && snapshot, 'wishlist_snapshot_required');
      const session = await this.bootstrap(), generation = this.generation;
      requireValue(session.accountPartition === previous.accountPartition && session.contextHash === previous.contextHash, 'wishlist_session_changed');
      if (safeAction.version === 2) requireValue(safeAction.country === this.country, 'wishlist_action_invalid');
      const record = { version: 1, partition: session.accountPartition, contextHash: session.contextHash,
        envelope: { ticket: snapshot.ticket, snapshotHash: snapshot.snapshotHash, action: safeAction } };
      await this.journal.claim(record); this.snapshot = null; this.guard(generation);
      let result;
      try { result = operationRecord(await this.send('mutate', record.envelope, session, generation)); }
      catch (error) { this.guard(generation); throw error; }
      requireValue(result.status !== 'pending', 'wishlist_operation_pending');
      const next = await this.readSnapshot(session, generation);
      await this.journal.settle(record); this.guard(generation); this.snapshot = next;
      return { snapshot: clone(next), outcome: result.status };
    });
  }

  catalog() {
    return this.exclusive(async () => {
      const previous = this.session, snapshot = this.snapshot;
      requireValue(previous && snapshot, 'wishlist_snapshot_required');
      const session = await this.bootstrap(), generation = this.generation;
      requireValue(previous.accountPartition === session.accountPartition && previous.contextHash === session.contextHash, 'wishlist_session_changed');
      const entries = []; let offset = 0, total;
      do {
        const page = await this.send('catalog', { snapshotHash: snapshot.snapshotHash, country: this.country, offset }, session, generation);
        requireValue(exact(page, ['schemaVersion', 'snapshotHash', 'offset', 'total', 'nextOffset', 'status', 'country', 'entries']) &&
          page.schemaVersion === 1 && page.snapshotHash === snapshot.snapshotHash && page.status === 'ready' && page.country === this.country &&
          page.offset === offset && page.total === snapshot.wishlist.memberships.length && (total === undefined || page.total === total) &&
          Array.isArray(page.entries) && page.entries.length === Math.min(100, page.total - offset) &&
          page.nextOffset === (offset + page.entries.length < page.total ? offset + page.entries.length : null));
        total = page.total;
        for (let i = 0; i < page.entries.length; i++) {
          const entry = page.entries[i], member = snapshot.wishlist.memberships[offset + i];
          requireValue(object(entry) && entry.categoryId === member.categoryId && entry.productId === member.productId && entry.variantId === member.variantId &&
            ['visible', 'unavailable'].includes(entry.status));
          entries.push(entry);
        }
        offset = page.nextOffset;
      } while (offset !== null);
      this.guard(generation); return clone({ snapshot, country: this.country, entries });
    });
  }
}
return { WishlistClient: WishlistClient, WishlistJournal: WishlistJournal, WishlistFailure: WishlistFailure }; // mdvWishWorker-1 packaging (P1)
})(); // mdvWishWorker-1 packaging (P1)

/* ---------------------------------------------------------------------------------------------------------------
   (b) mdvWishT (mdvWishWorker-1): the one door between the live renderer (c) and the vendored client (a).
   - Serial queue: every client call runs one at a time, so the client's exclusive() never reports wishlist_busy.
   - Merged reads: callers share the read that is queued or in flight; a read within 10 s of the last server read
     or write reuses that result after ONE bootstrap confirms the same signed-in account (Amendment 1 F3); an account
     change drops it. read({fresh: true}), a stale model and drop() (tab back in the foreground) read in full.
   - Intents: add/remove taps are recorded per product; a tap superseded by a later tap on the same product before
     its turn sends nothing. Writes are never retried automatically (the client's journal recovers a lost reply).
   - Add: into the customer's list with the numerically lowest id; with no list yet, category.create "FAVOURITE"
     first, then product.add (v2). Remove: one product.remove per list holding the product, one after another,
     stopping at the first that is not applied; it targets every membership of the product's local ids, including
     memberships whose catalogue entry is unavailable (Amendment 1 F5). Both report the server truth afterwards; an
     APPLIED write stays applied in the UI even if the follow-up catalogue/model step fails (Amendment 1 F4).
   - Errors: auth (401/403), rate (429, Retry-After 1-60 s back-off), changed (session changed), pending, and
     unavailable for everything else (incl. Shopify's HTML 500). Never any fallback to another wishlist backend.
   - Memory only: snapshot, catalogue entries and the legacy-shaped view live in this closure; nothing is written
     to localStorage or sessionStorage (the one exception is the opt-in debug trace below). No customer id, email or
     name is ever sent: the signed proxy identifies the customer.
   - Bugfix (11 Oct 2026, CA phone QA): a product this tab has seen visible keeps its local ids for the page's life,
     so remove-everywhere still targets it when a later catalogue read reports it unavailable (it never answers
     "absent" with nothing sent while the server holds it). A storage failure (e.g. WebKit's lost IndexedDB
     connection) retires the client, so the next call opens a fresh journal connection; nothing is retried.
     An applied add also records the product's new local id from the write's own snapshot (bugfix 2, F3).
   - Opt-in debug trace: off unless the URL has mdvwishdebug=1, which is remembered for 24 hours (localStorage
     mdvWishDebug holds the expiry time; mdvwishdebug=0, or an expired or older-format value, forgets it and the trace).
     The last 30 facade events (product id, intent, outcome, sent count, error codes, times, a coarse page type and
     signed in/out) are kept in this tab's sessionStorage and shown in a small fixed overlay. Never a path, query,
     customer id, email, CSRF value or token (account links carry a customer id and token in the path). The trace is
     cleared on every account change or invalidation, and when the page's signed-in state changes between pages.
   --------------------------------------------------------------------------------------------------------------- */
var mdvWishT = (function () {
  'use strict';
  var MARKER = 'mdvWishWorker-1';
  var REUSE_MS = 10000;
  var ID = /^[1-9][0-9]{0,18}$/;
  var HANDLE = /^[a-z0-9][a-z0-9-]{0,254}$/;
  var cfg;
  var client = null, model = null, modelAt = 0, stale = true;
  var queue = Promise.resolve(), pending = null, pendingFresh = false;
  var backoffUntil = 0, seq = 0, intents = Object.create(null), listeners = [], cache = new Map(), known = new Map();

  function now() { return Date.now(); }

  // ------------------------------------------------------------------------------------- opt-in debug trace
  var TRACE_KEY = 'mdvWishTrace', FLAG_KEY = 'mdvWishDebug', TRACE_MAX = 30, TRACE_TTL = 24 * 60 * 60 * 1000;
  var traceOn = null, traceMem = [], traceBox = null, traceOpen = true;

  // Bugfix 2 (F1): the flag is stored as its expiry time, 24 h after mdvwishdebug=1 was last opened. mdvwishdebug=0, an
  // expired value and the older value "1" (no expiry) all forget the flag and the stored trace.
  function debugOn() {
    if (traceOn !== null) { return traceOn; }
    var flag = null;
    try { flag = new URLSearchParams(window.location.search).get('mdvwishdebug'); } catch (error) { flag = null; }
    traceOn = flag === '1';   // with storage blocked, on for this page only
    try {
      var stored = window.localStorage.getItem(FLAG_KEY);
      if (flag === '1') { stored = String(now() + TRACE_TTL); window.localStorage.setItem(FLAG_KEY, stored); }
      var until = /^[0-9]{13}$/.test(stored || '') ? Number(stored) : 0;
      if (flag !== '0' && until > now() && until <= now() + TRACE_TTL) {
        traceOn = true;
      } else if (stored !== null || flag === '0') {
        traceOn = false;
        window.localStorage.removeItem(FLAG_KEY);
        window.sessionStorage.removeItem(TRACE_KEY);
      }
    } catch (error) {}
    return traceOn;
  }

  function traceClear() {
    traceMem = [];
    try { window.sessionStorage.removeItem(TRACE_KEY); } catch (error) {}
  }

  // Bugfix 2 (F1): a coarse page type, never the path or query (/account/reset/<customer id>/<token> and
  // /account/activate/... carry a customer id and a token).
  function pageType() {
    var parts = [];
    try { parts = String(window.location.pathname || '').toLowerCase().split('/').filter(Boolean); } catch (error) { parts = []; }
    if (parts.length && /^[a-z]{2}(-[a-z0-9]{2,4})?$/.test(parts[0])) { parts.shift(); }   // locale prefix (/fr/..., /en-ca/...)
    var wishlist = window.wsapps && typeof window.wsapps.pageURL === 'string' ? window.wsapps.pageURL.toLowerCase() : 'wishlist';
    var first = parts[0] || '';
    if (first === 'account' || first === 'customer_authentication') { return 'account'; }
    if (first === 'pages' && parts[1] === wishlist) { return 'wishlist'; }
    if (first === 'products' || (first === 'collections' && parts[2] === 'products')) { return 'product'; }
    if (first === 'collections') { return 'collection'; }
    if (first === 'cart') { return 'cart'; }
    return 'other';
  }

  // Signed in or out, from the page's own hint (never the id itself). Used only to clear the trace when it changes.
  function signedState() {
    return window.wsapps && window.wsapps.customerId ? 'in' : 'out';
  }

  // Only these fields are ever recorded: product id (public), intent, outcome, counts, error codes, times, page type
  // (wishlist / product / collection / cart / account / other) and signed in/out.
  var TRACE_FIELDS = ['pid', 'want', 'outcome', 'sent', 'code', 'cause', 'ms', 'items', 'n', 'page', 'signed'];

  function traceEntry(base, fields) {
    TRACE_FIELDS.forEach(function (key) {
      if (fields && fields[key] !== undefined && fields[key] !== null) { base[key] = fields[key]; }
    });
    return base;
  }

  // Bugfix 2 (F1): a stored trace is read back through the same allow-list, so anything else in it (e.g. the "path" of
  // an older build's trace, which could hold an account link) is dropped and never written back or shown.
  function traceRead() {
    try {
      var stored = JSON.parse(window.sessionStorage.getItem(TRACE_KEY) || '[]');
      if (Array.isArray(stored)) {
        return stored.filter(function (entry) { return entry && typeof entry === 'object'; }).slice(-TRACE_MAX).map(function (entry) {
          return traceEntry({ t: String(entry.t || ''), ev: String(entry.ev || '') }, entry);
        });
      }
    } catch (error) {}
    return traceMem.slice(-TRACE_MAX);
  }

  function trace(type, fields) {
    if (!debugOn()) { return; }
    var entry = traceEntry({ t: new Date().toISOString().slice(11, 23), ev: type }, fields);
    var list = traceRead().concat([entry]).slice(-TRACE_MAX);
    traceMem = list;
    try { window.sessionStorage.setItem(TRACE_KEY, JSON.stringify(list)); } catch (error) {}
    traceDraw();
  }

  function traceHeader() {
    var theme = window.Shopify && window.Shopify.theme ? window.Shopify.theme : null;
    return 'theme ' + (theme ? String(theme.id) + ' (' + String(theme.role) + ')' : '?') + ' | script: worker ' + MARKER + ' | ' +
      window.location.host;
  }

  function traceLine(entry) {
    return [entry.t, entry.ev, entry.pid ? 'p' + entry.pid : '', entry.want || '', entry.outcome || '',
      entry.sent !== undefined ? 'sent=' + entry.sent : '', entry.code ? 'err=' + entry.code : '', entry.cause || '',
      entry.items !== undefined ? 'items=' + entry.items : '', entry.n !== undefined ? 'n=' + entry.n : '',
      entry.ms !== undefined ? entry.ms + 'ms' : '', entry.page || '', entry.signed ? 'signed-' + entry.signed : ''].filter(Boolean).join(' ');
  }

  // Shopify's theme-preview bar sits in the browser's top layer at the bottom of the screen and would hide the overlay:
  // keep the overlay just above it when it is there.
  function traceBottom() {
    var bar = document.getElementById('PBarNextFrameWrapper') || document.getElementById('preview-bar-iframe');
    var height = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    return height > 0 && height < window.innerHeight / 2 ? height : 0;
  }

  function traceDraw() {
    if (!debugOn() || !document.body) { return; }
    if (!traceBox) {
      traceBox = document.createElement('div');
      traceBox.id = 'mdv-wish-debug';
      traceBox.setAttribute('data-mdv-wish-debug', MARKER);
      traceBox.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;max-height:38vh;overflow:auto;' +
        'background:rgba(0,0,0,.94);color:#fff;font:10px/1.35 ui-monospace,Menlo,monospace;padding:4px 6px;white-space:pre-wrap;' +
        'word-break:break-all;text-align:left;text-transform:none;letter-spacing:0;';
      traceBox.addEventListener('click', function () { traceOpen = !traceOpen; traceDraw(); });
      document.body.appendChild(traceBox);
    }
    traceBox.style.bottom = traceBottom() + 'px';
    var lines = traceRead().slice().reverse().map(traceLine);
    traceBox.textContent = traceHeader() + '\n' + (traceOpen ? lines.join('\n') : lines.length + ' events (tap to show)');
  }

  function traceStart() {
    if (!debugOn()) { return; }
    // Bugfix 2 (F4): a sign-out or sign-in since the last page means another customer may be using this tab.
    var signed = signedState(), last = traceRead().filter(function (entry) { return !!entry.signed; }).pop();
    if (last && last.signed !== signed) { traceClear(); }
    trace('load', { page: pageType(), signed: signed });
    window.addEventListener('pagehide', function () {
      var open = Object.keys(intents).length;
      trace('pagehide', { n: open, page: pageType(), signed: signed });
    });
    if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', traceDraw); } else { traceDraw(); }
    setTimeout(traceDraw, 2000); setTimeout(traceDraw, 6000);   // the preview bar arrives late
  }

  function causeOf(error) {
    var code = error && error.code;
    return typeof code === 'string' && /^[a-z_]{1,64}$/.test(code) ? code : (error && error.name ? String(error.name).slice(0, 40) : '');
  }

  function fault(code, retryAfter) {
    var error = new Error('mdvWishT ' + code);
    error.mdvWish = code;
    if (retryAfter) { error.retryAfter = retryAfter; }
    return error;
  }

  function classify(error) {
    if (error && error.mdvWish) { return error; }
    var code = error && error.code;
    if (code === 'wishlist_authentication_required') { return fault('auth'); }
    if (code === 'wishlist_rate_limited') { return fault('rate', error.retryAfter); }
    if (code === 'wishlist_session_changed') { return fault('changed'); }
    if (code === 'wishlist_operation_pending' || code === 'wishlist_busy') { return fault('pending'); }
    return fault('unavailable');
  }

  // Bugfix: WebKit can lose a page's IndexedDB connection (e.g. after the tab was suspended) without a close event; every
  // journal step of that client then fails until a reload. Retire the client so the next call builds a fresh one with a
  // fresh journal connection. The failed call is not retried, and a pending journal record stays in IndexedDB for the
  // next read to recover, as before.
  function retire() {
    var old = client;
    client = null;
    trace('client', { cause: 'retired after wishlist_storage_unavailable' });
    try { if (old && old.journal) { old.journal.close().catch(function () {}); } } catch (error) {}
  }

  function note(error) {
    var failure = classify(error);
    if (error && !error.mdvWish && !failure.cause) { failure.cause = causeOf(error); }
    stale = true;
    if (error && error.code === 'wishlist_storage_unavailable' && client) { retire(); }
    if (failure.mdvWish === 'rate') {
      var seconds = Math.min(60, Math.max(1, Number(failure.retryAfter) || 60));
      failure.retryAfter = seconds;
      backoffUntil = Math.max(backoffUntil, now() + seconds * 1000);
    }
    return failure;
  }

  function config() {
    if (cfg !== undefined) { return cfg; }
    cfg = null;
    try {
      var element = document.getElementById('mdv-wishlist-config');
      var value = element ? JSON.parse(element.getAttribute('data-config') || 'null') : null;
      if (value && typeof value === 'object' && typeof value.source === 'string' && typeof value.proxyPath === 'string' &&
        typeof value.country === 'string') { cfg = value; }
    } catch (error) { cfg = null; }
    return cfg;
  }

  function label(key) {
    var c = config(), labels = c && c.labels;
    return labels && typeof labels[key] === 'string' ? labels[key] : '';
  }

  function invalidated() {
    model = null; stale = true; cache.clear(); known.clear();
    if (debugOn()) { traceClear(); }   // bugfix 2 (F4): nothing of the previous session stays in the debug trace either
    trace('invalidated', {});
    listeners.slice().forEach(function (listener) { try { listener(); } catch (error) {} });
  }

  function ensure() {
    if (client) { return client; }
    var c = config();
    if (!c) { throw fault('unavailable'); }
    try {
      client = new MdvWishClient.WishlistClient({ source: c.source, proxyPath: c.proxyPath, country: c.country,
        origin: window.location.origin, onInvalidate: invalidated });
    } catch (error) { throw fault('unavailable'); }
    return client;
  }

  function enqueue(job) {
    var run = queue.then(function () { return job(); });
    queue = run.then(function () {}, function () {});
    return run;
  }

  function backoff() {
    var left = backoffUntil - now();
    return left > 0 ? new Promise(function (resolve) { setTimeout(resolve, left); }) : Promise.resolve();
  }

  // Numeric order of two decimal ids, compared as strings (safe past 2^53).
  function cmpId(a, b) {
    a = String(a); b = String(b);
    if (a.length !== b.length) { return a.length < b.length ? -1 : 1; }
    return a < b ? -1 : (a > b ? 1 : 0);
  }

  function cmpText(a, b) { return a < b ? -1 : (a > b ? 1 : 0); }

  // Newest (largest) first; a missing value sorts last.
  function newest(a, b, compare) {
    var noA = a === undefined || a === null || a === '', noB = b === undefined || b === null || b === '';
    if (noA) { return noB ? 0 : 1; }
    if (noB) { return -1; }
    return compare(b, a);
  }

  function gid(value, type) {
    var match = typeof value === 'string' && new RegExp('^gid://shopify/' + type + '/([1-9][0-9]{0,18})$').exec(value);
    return match ? match[1] : '';
  }

  function esc(value) {
    return String(value === undefined || value === null ? '' : value).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function memberKey(member) { return member.productId + '|' + (member.variantId === null ? '' : member.variantId); }

  async function modelFor(snapshot, fresh) {
    var members = snapshot.wishlist.memberships;
    if (members.length && (fresh || members.some(function (member) { return !cache.has(memberKey(member)); }))) {
      var catalog = await client.catalog();
      if (catalog.snapshot.snapshotHash !== snapshot.snapshotHash) { throw fault('changed'); }
      catalog.entries.forEach(function (entry, index) { cache.set(memberKey(members[index]), entry); });
    }
    model = { snapshot: snapshot, entries: members.map(function (member) { return cache.get(memberKey(member)) || null; }) };
    model.entries.forEach(function (entry, index) { learn(members[index], entry); });
    modelAt = now(); stale = false;
    return model;
  }

  // Bugfix: remember, for this page's life, which local product ids a Shopify product was seen visible under. A later
  // catalogue answer of "unavailable" for that membership overwrites the cache entry but not this, so remove-everywhere
  // can still target it. Cleared with everything else on an account change (invalidated).
  function learn(member, entry) {
    var productId = visibleProduct(entry);
    if (!productId) { return; }
    var ids = known.get(productId);
    if (!ids) { ids = Object.create(null); known.set(productId, ids); }
    ids[member.productId] = 1;
  }

  // Bugfix 2 (F3): an applied add also teaches the product's local id straight from the write's own snapshot - the one
  // new membership of the target list - so a later remove can target it even when every follow-up catalogue read failed
  // and the catalogue then reports the product unavailable. The ticket fences every other change, so a single new local
  // product is this write; anything else (none, several, or a local id already seen visible as another product) is
  // left alone.
  function learnAdded(before, after, categoryId, productId) {
    var had = Object.create(null), fresh = [];
    before.wishlist.memberships.forEach(function (member) { if (member.categoryId === categoryId) { had[memberKey(member)] = 1; } });
    after.wishlist.memberships.forEach(function (member) {
      if (member.categoryId === categoryId && !had[memberKey(member)] && fresh.indexOf(member.productId) === -1) { fresh.push(member.productId); }
    });
    if (fresh.length !== 1) { return; }
    var clash = false;
    cache.forEach(function (entry, key) {
      var seen = visibleProduct(entry);
      if (seen && seen !== productId && key.split('|')[0] === fresh[0]) { clash = true; }
    });
    known.forEach(function (ids, seen) { if (seen !== productId && ids[fresh[0]]) { clash = true; } });
    if (clash) { return; }
    var ids = known.get(productId);
    if (!ids) { ids = Object.create(null); known.set(productId, ids); }
    ids[fresh[0]] = 1;
  }

  async function refresh() {
    ensure();
    var result = await client.refresh();
    return modelFor(result.snapshot, true);
  }

  // A write uses the snapshot ticket it was decided on. Every server snapshot read (another tab, the app) fences older
  // tickets, so a model older than the merged-read window is refreshed first (a read, never a write retry).
  async function current() {
    if (!model || stale || !client || !client.session || !client.snapshot || now() - modelAt >= REUSE_MS) {
      await refresh();
      return { model: model, fresh: true };
    }
    return { model: model, fresh: false };
  }

  function visibleProduct(entry) {
    return entry && entry.status === 'visible' && entry.product ? gid(entry.product.id, 'Product') : '';
  }

  // Amendment 1 F5: the local product ids a Shopify product maps to, from this snapshot's visible entries and from
  // any catalogue entry already seen in this tab (cache keys are "localProductId|localVariantId").
  function localIds(m, productId) {
    var ids = Object.create(null);
    m.snapshot.wishlist.memberships.forEach(function (member, index) {
      if (visibleProduct(m.entries[index]) === productId) { ids[member.productId] = 1; }
    });
    cache.forEach(function (entry, key) { if (visibleProduct(entry) === productId) { ids[key.split('|')[0]] = 1; } });
    var learned = known.get(productId);
    if (learned) { Object.keys(learned).forEach(function (id) { ids[id] = 1; }); }   // bugfix: seen visible earlier
    return ids;
  }

  // Every membership of those local products, whatever its own catalogue status (e.g. a hidden variant): remove-
  // everywhere must leave nothing behind, and "saved" means at least one such membership remains.
  function holding(m, productId) {
    var ids = localIds(m, productId), seen = Object.create(null), out = [];
    m.snapshot.wishlist.memberships.forEach(function (member) {
      if (!ids[member.productId]) { return; }
      var key = member.categoryId + '|' + member.productId;
      if (!seen[key]) { seen[key] = 1; out.push({ categoryId: member.categoryId, productId: member.productId }); }
    });
    return out.sort(function (a, b) { return cmpId(a.categoryId, b.categoryId) || cmpId(a.productId, b.productId); });
  }

  function saved(m, productId) {
    return !!m && holding(m, productId).length > 0;
  }

  // Amendment 1 F4: after a write the server has answered, the follow-up catalogue/model step may fail. That must not
  // undo the write in the UI: the model is marked stale (the next read reconciles) and the caller keeps the outcome.
  async function settle(snapshot) {
    try {
      await modelFor(snapshot, false);
      return true;
    } catch (error) {
      note(error);
      model = null;
      return false;
    }
  }

  function defaultCategory(snapshot) {
    var best = null;
    snapshot.wishlist.categories.forEach(function (category) {
      if (ID.test(category.id) && (best === null || cmpId(category.id, best) < 0)) { best = category.id; }
    });
    return best;
  }

  // toLegacyShape: the server truth in the shape the live renderer reads ({data: {categories: [{id, category_name,
  // products: [...]}]}}). Only catalogue-visible members are rendered (the legacy renderer hid products whose page
  // was gone); text is HTML-escaped because the renderer builds markup from it.
  function legacy(m) {
    var wishlist = m.snapshot.wishlist;
    if (!wishlist.lists.length && !wishlist.categories.length) {
      return { error: true, status: 404, message: 'Wishlist not found.', data: null };
    }
    var byCategory = Object.create(null);
    wishlist.categories.forEach(function (category) { byCategory[category.id] = []; });
    wishlist.memberships.forEach(function (member, index) {
      var entry = m.entries[index], productId = visibleProduct(entry);
      if (!productId || !byCategory[member.categoryId] || !HANDLE.test(entry.product.handle || '')) { return; }
      var image = entry.product.imageUrl || (entry.variant && entry.variant.imageUrl) || '';
      byCategory[member.categoryId].push({
        id: member.productId, cid: member.categoryId, savedAt: member.savedAt, mdv_variant_local: member.variantId,
        shopify_product_id: productId, handle: entry.product.handle, title: esc(entry.product.title), image_url: esc(image),
        variants: { shopify_variant_id: entry.variant ? gid(entry.variant.id, 'ProductVariant') : '' },
      });
    });
    return { error: false, status: 200, message: 'Success', data: { categories: wishlist.categories.map(function (category) {
      return { id: category.id, category_name: esc(category.name === null ? '' : category.name), products: byCategory[category.id] };
    }) } };
  }

  // The one wishlist order (flat contract §3.2 with a per-item date): saved date newest first, then the local
  // product id, local variant id and list id, each largest first (the app's P3 keys); one card per Shopify product.
  function order(categories) {
    var all = [];
    (Array.isArray(categories) ? categories : []).forEach(function (category) {
      (category && Array.isArray(category.products) ? category.products : []).forEach(function (product) {
        if (!product) { return; }
        if (product.cid === undefined) { product.cid = category.id; }
        all.push(product);
      });
    });
    all.sort(function (a, b) {
      return newest(a.savedAt, b.savedAt, cmpText) || newest(a.id, b.id, cmpId) ||
        newest(a.mdv_variant_local, b.mdv_variant_local, cmpId) || newest(a.cid, b.cid, cmpId);
    });
    var seen = Object.create(null);
    return all.filter(function (product) {
      var key = String(product.shopify_product_id === undefined || product.shopify_product_id === null ? '' : product.shopify_product_id);
      if (!key || seen[key]) { return false; }
      seen[key] = 1;
      return true;
    });
  }

  function readTrace(started, result, error) {
    if (!debugOn()) { return; }
    if (error) { trace('read', { code: error.mdvWish, cause: error.cause, ms: now() - started }); return; }
    trace('read', { outcome: 'ok', items: result && result.data ? order(result.data.categories).length : 0, ms: now() - started });
  }

  function read(options) {
    var fresh = !!(options && options.fresh);
    if (pending) { if (fresh) { pendingFresh = true; } return pending; }
    pendingFresh = fresh;
    var started = now();
    var mine = enqueue(async function () {
      var wantFresh = pendingFresh;
      await backoff();
      if (!wantFresh && model && !stale && now() - modelAt < REUSE_MS) {
        // Amendment 1 F3: a cached snapshot is drawn only after a fresh bootstrap confirms the same signed-in account.
        // If the account changed (e.g. a login in another tab), the client invalidates: this model and every rendered
        // card are dropped and the read continues as a full server read below.
        try { await ensure().bootstrap(); } catch (error) { throw note(error); }
        if (model && !stale) { return legacy(model); }
      }
      try {
        await refresh();
      } catch (error) {
        var failure = note(error);
        if (failure.mdvWish !== 'changed') { throw failure; }
        try { await refresh(); } catch (again) { throw note(again); }
      }
      return legacy(model);
    });
    mine.then(function (result) { readTrace(started, result, null); }, function (error) { readTrace(started, null, error); });
    pending = mine;
    var done = function () { if (pending === mine) { pending = null; pendingFresh = false; } };
    mine.then(done, done);
    return mine;
  }

  function intent(productId, want, work) {
    var pid = String(productId === undefined || productId === null ? '' : productId);
    if (!ID.test(pid)) { return Promise.reject(fault('unavailable')); }
    if (now() < backoffUntil) { return Promise.reject(fault('rate', Math.ceil((backoffUntil - now()) / 1000))); }
    var mine = ++seq, label = want ? 'add' : 'remove', started = now();
    intents[pid] = { want: want, seq: mine };
    trace('intent', { pid: pid, want: label });
    return enqueue(async function () {
      var latest = intents[pid];
      if (!latest || latest.seq !== mine) { trace('superseded', { pid: pid, want: label }); return { superseded: true }; }
      try {
        if (now() < backoffUntil) { throw fault('rate', Math.ceil((backoffUntil - now()) / 1000)); }
        ensure();
        var result = await work(pid);
        trace('outcome', { pid: pid, want: label, outcome: result && result.outcome, sent: result && result.sent, ms: now() - started });
        return result;
      } catch (error) {
        var failure = note(error);
        trace('error', { pid: pid, want: label, code: failure.mdvWish, cause: failure.cause, ms: now() - started });
        throw failure;
      } finally {
        if (intents[pid] && intents[pid].seq === mine) { delete intents[pid]; }
      }
    });
  }

  async function doAdd(productId, variantId) {
    if (!ID.test(variantId)) { throw fault('unavailable'); }
    var state = await current(), target = defaultCategory(state.model.snapshot), decidedOn = state.model.snapshot;
    if (target === null) {
      var lists = state.model.snapshot.wishlist.lists;
      if (lists.length > 1) { throw fault('unavailable'); }
      trace('write', { pid: productId, want: 'create' });
      var created = await client.mutate({ type: 'category.create', listId: lists.length ? lists[0].id : null, name: 'FAVOURITE' });
      var createdSettled = await settle(created.snapshot);
      target = defaultCategory(created.snapshot);
      decidedOn = created.snapshot;
      if (created.outcome !== 'applied' || target === null) {
        if (!createdSettled) { throw fault('unavailable'); }
        return { saved: saved(model, productId), outcome: created.outcome === 'applied' ? 'not_applied' : created.outcome };
      }
    }
    trace('write', { pid: productId, want: 'add' });
    var result = await client.mutate({ version: 2, type: 'product.add', categoryId: target,
      shopifyProductId: 'gid://shopify/Product/' + productId, shopifyVariantId: 'gid://shopify/ProductVariant/' + variantId,
      country: config().country });
    if (result.outcome === 'applied') { learnAdded(decidedOn, result.snapshot, target, productId); }   // bugfix 2 (F3)
    var settled = await settle(result.snapshot);
    // F4: an applied add is saved, even if the follow-up catalogue/model step failed (the model is stale then).
    if (result.outcome === 'applied') { return { saved: true, outcome: 'applied' }; }
    if (!settled) { throw fault('unavailable'); }   // not applied and the server truth is unknown: the caller reverts
    return { saved: saved(model, productId), outcome: result.outcome };
  }

  async function doRemove(productId) {
    var state = await current(), targets = holding(state.model, productId), settled = true;
    if (!targets.length && !state.fresh) { await refresh(); targets = holding(model, productId); }
    for (var index = 0; index < targets.length; index++) {
      trace('write', { pid: productId, want: 'remove', n: index + 1 });   // debug only: the write is about to be sent
      var result = await client.mutate({ type: 'product.remove', categoryId: targets[index].categoryId, productId: targets[index].productId });
      settled = await settle(result.snapshot);
      if (result.outcome !== 'applied') {
        if (!settled) { throw fault('unavailable'); }
        return { removed: !saved(model, productId), outcome: result.outcome, sent: index + 1 };
      }
    }
    // F4: when every remove was applied the product is gone, even if the last catalogue/model step failed.
    return { removed: settled ? !saved(model, productId) : true, outcome: targets.length ? 'applied' : 'absent', sent: targets.length };
  }

  var api = {
    marker: MARKER,
    configured: function () { return !!config(); },
    label: label,
    read: read,
    add: function (productId, variantId) {
      return intent(productId, true, function (pid) { return doAdd(pid, String(variantId === undefined || variantId === null ? '' : variantId)); });
    },
    removeEverywhere: function (productId) { return intent(productId, false, doRemove); },
    order: order,
    legacy: function () { return model ? legacy(model) : null; },
    // Bugfix 2 (F6): whether a fresh server model still holds the product (true/false); null without one.
    held: function (productId) {
      var pid = String(productId === undefined || productId === null ? '' : productId);
      return model && !stale && ID.test(pid) ? saved(model, pid) : null;
    },
    invalidate: function () { trace('invalidate', {}); stale = true; if (client) { client.invalidate(); } else { invalidated(); } },
    // Amendment 1 F3: drop the cached snapshot (keep the session) so the next read is a full server read.
    drop: function () { trace('drop', {}); model = null; stale = true; },
    onInvalidate: function (listener) { if (typeof listener === 'function') { listeners.push(listener); } },
    debug: function () {
      return { stale: stale, hasModel: !!model, modelAgeMs: model ? now() - modelAt : null, backoffMs: Math.max(0, backoffUntil - now()),
        session: !!(client && client.session), reading: !!pending, intents: Object.keys(intents).length };
    },
    // The opt-in debug trace (a copy; [] when the trace is off).
    trace: function () { return debugOn() ? traceRead() : []; },
  };
  traceStart();
  return api;
})();

/* (c) live flat renderer (mdvWishFlat-1), routed through mdvWishT */
// Selectors and variables
var wsAddtocartForm = [
  "#shopify-section-product-template form[action$='/cart/add']",
  "form.product-purchase-form[action^='/cart/add']",  
  "form.shopify-product-form[action^='/nl/cart/add']",
  "form.shopify-product-form[action^='/fr/cart/add']",
  "form.shopify-product-form[action^='/de/cart/add']",
  "form.shopify-product-form[action^='/cart/add']",
  ".product-form form[action$='/cart/add.js']",
  ".product-form form[action$='/cart/add']"
].join(', ');


var addToWishlistCollection = '.cs_ws_icon';
var addToWishlistProduct = '#ws-fav-html';
var wsAddtocartBtn = "button[name='add'], input[name='add'], [name='add']";
var wsAddtocartBtnClass = "btn btn1 pr_btn btn--full btn--secondary w-full button button--primary button--filled button--large button--primary__filled _button--secondary _button--primary action_button button--full-width";
var wsBtnID = "ws-fav-html";
var wsBtnText = "Add to Wishlist";
var wsBtnTextWishlist = "In Wishlist";
var icon = window.wsapps.wishlist_icon;
var fillicon = window.wsapps.wishlist_icon_fill;
var latestCategories = '';
var ajaxcustomer_id = window.wsapps.customerId;
var ajaxcustomer_name = window.wsapps.customerName;
var ajaxcustomer_email = window.wsapps.customerEmailId;
var pageUrl = `/pages/${window.wsapps.pageURL}`;
// mdvWishWorker-1 (F-C step 2): wishlist handlers find the star button by class; the id stays as the fallback for the
// PDP and collection-grid buttons, which have no class twin (sections/snippets are outside F-C step 1).
var mdvWsFavBtnSel = '.mdv-ws-fav-btn, #ws-fav-btn';
if(window.wsapps.wishlist_status == '1') { 
  function setLoader() {
    //console.log('setLoader ======================================================');
    var CSWishlistPage = document.getElementsByClassName("CS-Wishlist-page");
    if(CSWishlistPage.length > 0){
      for (var i = 0; i < CSWishlistPage.length; i++) {
        if (!mdvWishPageOk(CSWishlistPage[i])) { continue; } // mdvWishWorker-1
        CSWishlistPage[i].innerHTML = '<div class="EmptyPageData"><img src="https://cdn.shopify.com/s/files/1/0610/9642/7715/files/Web.gif?v=1709644593" /></div>';
      }
    }
  }
  function removeLoader() {
    //console.log('removeLoader ======================================================');
    $("#CS-Wishlist-page .EmptyPageData").remove();
  }
  // Get the current URL
  var currentURL = window.location.href;
  function wishlistHTML(data){
    //console.log('wishlistHTML ======================================================');
    var wishlistHTML = document.getElementsByClassName('CS-Wishlist-page');
    if(wishlistHTML.length > 0){
      for (var i = 0; i < wishlistHTML.length; i++) {
        if (!mdvWishPageOk(wishlistHTML[i])) { continue; } // mdvWishWorker-1
        wishlistHTML[i].innerHTML = '';
        wishlistHTML[i].innerHTML += data;
      }
    }
  }
  
  function getUrlParameter(name) {
    // console.log('getUrlParameter ======================================================');
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    var results = regex.exec(currentURL);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  }    
  
  // Extract values for from_user_id and list_id
  var fromUserId = getUrlParameter('from_user_id');
  var listId = getUrlParameter('list_id');
  
  function CSCategoryGetSingle(catId) {
    //console.log('CSCategoryGetSingle ====================================================== API');
    // mdvWishFlat-1: one wishlist per customer. A list link (?list_id=) renders the whole wishlist - every list, newest
    // first, one card per product - not one list (catId is ignored).
    // mdvWishWorker-1: the signed-in customer's own wishlist only, read through mdvWishT; shared-list links
    // (?from_user_id=) are retired and fetch nothing. (CSRetrieveWsDataCategory, only used by shared links, is removed.)
    if (fromUserId) { mdvWishSharedUnavailable(); return; }
    mdvWishT.read()
    .then(result => {
      if (result && typeof result.data === 'object' && result.data !== null && Array.isArray(result.data.categories)) {
        mdvWishRemember(result.data.categories);
        var mdvFlat = mdvWishFlatten(result.data.categories);
        var productList = mdvFlat.products.slice();
        productList["title"] = '';
        if(productList.length > 0){
          CSRetrieveWsDataCategoryShow(productList, 'drawer', mdvFlat.category ? mdvFlat.category.id : catId)
          if(listId){
            //console.log('15 =================');
            CSRetrieveWishlistData('drawer');
          }
        }else{
          window.location.href = "/pages/"+window.wsapps.pageURL;
        }
      }
    })
    .catch(error => {
      mdvWishFail(error, 'page');
    });
  }
  
  // Category Header
  function CScategoryHeader(key){    
    //console.log('CScategoryHeader ======================================================');
    var CScategoryHeader = `<div class="cs-list-header-title"><h4>${key.toLowerCase()}</h4></div>`;      
    return CScategoryHeader
  }

  
  // Particular category in detail
  async function CSRetrieveWsDataCategoryShow(productList, drawer, catId) { 
    //console.log('CSRetrieveWsDataCategoryShow ======================================================');
    var htmlNew = `<div class="button_back"><a href="/pages/${window.wsapps.pageURL}"></a><div class="productListtitle">${productList.title.toLowerCase()}</div></div><div class="productHandlesByCategory productHandlesByCategory-134 productHandlesByCategory-${catId} category-id-${productList.title.toLowerCase()}" data-name="${productList.title.toLowerCase()}" id="category-id-Seci" data-cat-id="${catId}">`;
    htmlNew += `<div class="category-list-wrapper"><div class="category-list ${window.wsapps.parentClass}">`;
    productList.slice().forEach(product => {
      // mdvWishIds-1: the card star also carries class mdv-ws-fav-btn (id ws-fav-btn unchanged)
      htmlNew += `
      <div class="111 = cross-sell-product-item-drawer cross-sell-product-item cross-sell-item main-product-item two_column_item" data-handle="${product.handle}" data-id="${product.variants.shopify_variant_id}">
       <div class="cross-sell-product-image">
          <a href="/products/${product.handle}">
            <img src="${product.image_url}" alt="${product.title}" loading="lazy">
          </a>
       </div>
       <div class="cross-sell-product-content">
          <div class="cross-sell-product-price">
             <div class="cs-product-title">
                <a href="/products/${product.handle}">${product.title}</a>
             </div>
             <div class="cs_ws_icon" data-id="${product.shopify_product_id}" data-handle="${product.handle}" data-varid="${product.variants.shopify_variant_id}">
                <button aria-label="Add to Wishlist" id="ws-fav-btn" type="button" class="action_button  ws-active mdv-ws-fav-btn">
                   <span class="cs-wishlist-icon">
                      <svg width="25" height="21" viewBox="0 0 23 21" fill="none" xmlns="https://www.w3.org/2000/svg">
                         <path d="M11.5801 0.992188L14.668 7.2511L21.5801 8.2606L16.5801 13.13L17.7678 20.0183L11.5801 16.7642L5.39243 20.0183L6.58008 13.13L1.58008 8.2606L8.49219 7.2511L11.5801 0.992188Z" stroke="#333333" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                      </svg>
                   </span>
                </button>
             </div>
          </div>          
       </div>
    </div>`;      
    })
    
    htmlNew += '</div></div></div>';    
    if(drawer){
      var wishlistHTML1 = document.querySelectorAll('.main-wrapper .CS-Wishlist-page');
      wishlistHTML1.forEach(function(element) {
        if (!mdvWishPageOk(element)) { return; } // mdvWishWorker-1
        element.innerHTML = '';
        element.innerHTML += htmlNew;
      });
    }else{
      wishlistHTML(htmlNew)
    }
    if(location.pathname.includes('/collections/')){ 
      wishlistBtnCollection(productList);
    }
    formData();
    
  }

  
  function CSRetrieveWishlistProData() {
    //console.log('CSRetrieveWishlistProData ====================================================== API');
    // mdvWishWorker-1: the PDP star reads the signed-in wishlist through mdvWishT (a fresh server read); no customer or
    // product id is sent. The lists holding this product come from that snapshot.
    return mdvWishT.read({ fresh: true })
    .then(result => {
      var mdvCategories = result && result.data && Array.isArray(result.data.categories) ? result.data.categories : [];
      var mdvHolding = mdvWishIdsHolding(mdvCategories, window.wsapps.product_id);
      var csWsIconPro = document.querySelector(addToWishlistProduct);
      let spanbutton = csWsIconPro && csWsIconPro.querySelector('button');
      if(csWsIconPro && mdvHolding.length){
        csWsIconPro.setAttribute('data-categoryId', mdvHolding[0]);
        csWsIconPro.setAttribute('data-varId-dddd', 'window.wsapps.variant_id');
        csWsIconPro.setAttribute('data-varId', window.wsapps.variant_id);
        csWsIconPro.setAttribute('data-handle', window.wsapps.product_handle);
        if (spanbutton) {
          spanbutton.classList.add('ws-active');
        }
        let span = csWsIconPro.querySelector('span');
        if (span) {
          span.innerHTML = fillicon;
        }
      }else{
        if (csWsIconPro) {
          csWsIconPro.classList.remove('ws-active');
          csWsIconPro.removeAttribute('data-categoryId');
        }
        wishlistBtnPDP();
      }
      return true;
    })
    .catch(error => {
      mdvWishFail(error, 'read');
      return false;
    })
    .then(mdvRead => {
      if (mdvRead) { return CSRetrieveWishlistData('drawer'); }
    });
  }



  /* Wishlist Retrieve Wishlist Data */
  function CSRetrieveWishlistData(drawer, customer_id) {
    //console.log('CSRetrieveWishlistData ====================================================== Main function Disha  API');
    // mdvWishWorker-1: read through mdvWishT; customer_id is ignored (the signed proxy identifies the customer).
    if (!mdvWishKeepVisible) { $('div.productHandlesByCategory').hide() } // mdvWishGrid-1 W7
    // $('.cs-wishlist-wrapper').html('')
    return mdvWishT.read()
    .then(result => {
      $('div.productHandlesByCategory').show()
      if (result && typeof result.data === 'object' && result.data !== null && Array.isArray(result.data.categories)) {
        var mainArray = result.data.categories;
        mdvWishRemember(mainArray); // mdvWishFlat-1: last snapshot of the signed-in customer
        if (Array.isArray(mainArray) && mainArray.length > 0) {
          latestCategories = mdvWishDefaultId(mainArray); // mdvWishFlat-1: the default list is the lowest id (was the last list)
        }
        var productList = []
        for (const obj of mainArray) {
          for (var product of obj.products) {
            product["cid"] = obj.id;
            productList.push(product)
          }
        }
        if(drawer){
          CSWishlistDrawerData(result, productList);
        }else{
          if(location.pathname.includes('/collections/')){
            wishlistBtnCollection(productList);
          }
          if(location.pathname.includes('/products/')){
            wishlistBtnPDP(productList);
          }
          if($('.main-wrapper .CS-Wishlist-page').length > 0){
            if(!listId){
              CSWishlistPageData(result, productList);
              CSWishlistDrawerData(result, productList);
            }else{
              CSCategoryGetSingle(listId);
              CSWishlistDrawerData(result, productList);
            }
          }
        }
        if(mainArray.length === 0){
          var productHandles = document.querySelector('.productHandlesByCategory[data-name="newCategory"]');
          if (productHandles) {
              productHandles.style.display = 'none';
          }
        }
        //Get page data End
      } else {
        if(location.pathname.includes('/products/')){
          wishlistBtnPDP();
        }
        wishlistBtnCollection();
        emptyPage();
        var wishlistHTML = document.querySelectorAll('.main-wrapper .CS-Wishlist-page');
        if(wishlistHTML.length > 0){
          for (var i = 0; i < wishlistHTML.length; i++) {
            wishlistHTML[i].classList.add("wishlist-overflow");
          }
        }
      }
    })
    .catch(error => {
      mdvWishFail(error, drawer ? 'drawer' : 'page');
    });
  }

  var mdvWishTabRendersDrawer = false; // mdvWishGrid-1: true where the .cart_tab_link2 click re-renders the drawer
  var mdvWishKeepVisible = false; // mdvWishGrid-1 W7: skip the hide-all-lists step for post-tap reconciles
  var mdvWishSnap = null; // mdvWishFlat-1: last snapshot of the signed-in customer {categories} (memory only)
  var mdvWishPinsMem = {}; // mdvWishFlat-1: pin map used when localStorage is unavailable
  var mdvWishTabBound = false; // mdvWishWorker-1: the .cart_tab_link2 handler is bound once, even if mdvWishBoot re-runs

  function mdvWishBoot() {
  if(fromUserId){
    // mdvWishWorker-1: shared-list links are retired - the page shows a message, fetches nothing for the link and
    // never shows another wishlist in its place.
    mdvWishSharedUnavailable();
    if(ajaxcustomer_id && location.pathname.includes('/collections/')){
      //console.log('2 =================');
      CSRetrieveWishlistData('drawer');
    }
  }else if(ajaxcustomer_id){
    if(location.pathname.includes(pageUrl)){
      //console.log('3 =================');
      CSRetrieveWishlistData();
    } else if(location.pathname.includes('/collections/') && location.pathname.includes('/products/') || location.pathname.includes('/products/')){
       CSRetrieveWishlistProData()
      //CSRetrieveWishlistData('drawer');
    } else if(location.pathname.includes('/collections/')){
      //console.log('4 =================');
      CSRetrieveWishlistData('drawer');
    } else{
       mdvWishTabRendersDrawer = true;
       if (!mdvWishTabBound) {
         mdvWishTabBound = true;
         $('.cart_tab_link2').click(function(){
          //console.log('5 =================');
          CSRetrieveWishlistData('drawer');
        })
       }
    }

  }else{
    if(location.pathname.includes('/products/')){
      wishlistBtnPDP()
    }
    if(location.pathname.includes('/collections/')){
      wishlistBtnCollection()
    }
    emptyPage();
  }
  }
  mdvWishBoot();

  // mdvWishWorker-1: rendered wishlist data never outlives its session. The client invalidates on 401/403 and on an
  // account or context change; a bfcache restore and a logout click invalidate too. Each clears the drawer/page cards
  // and stars; a bfcache restore then boots again (fresh bootstrap).
  mdvWishT.onInvalidate(mdvWishClearRendered);
  // Amendment 1 F3: a tab back in the foreground never redraws a cached snapshot (another tab or the app may have
  // changed the account or the wishlist meanwhile); the next render reads in full from the server.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') { mdvWishT.drop(); }
  });
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) { mdvWishT.invalidate(); mdvWishBoot(); }
  });
  document.addEventListener('click', function (event) {
    var mdvLink = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (mdvLink && /\/account\/logout\/?$/.test(mdvLink.pathname || '')) { mdvWishT.invalidate(); }
  }, true);

  function emptyPage() {    
    //console.log('emptyPage ======================================================');
    var data = CSWSPageDataHeader();   
    data += emptyhtmlGrid();
    wishlistHTML(data);
    var elements = document.querySelectorAll('button#csws-create-list');
    for (var i = 0; i < elements.length; i++) {
        elements[i].style.display = 'none';
    }
  }
  /* Fetch Wishlist data on button click */
  function CSUpdateUI(target, PDPButton, newCreate) {
    //console.log('CSUpdateUI ======================================================  API');
    // mdvWishWorker-1: read through mdvWishT (reuses the result of the write that just finished).
    mdvWishT.read()
    .then(result => {
      var mainArray = result && result.data && Array.isArray(result.data.categories) ? result.data.categories : [];
      var productList = []
      for (const obj of mainArray) {
        for (var product of obj.products) {
          product["cid"] = obj.id;
          productList.push(product);
        }
      }
      var CSWishlistPage = document.getElementsByClassName("CS-Wishlist-page");
      if(CSWishlistPage && newCreate){
        CSWishlistPageData(result, productList);
        CSWishlistDrawerData(result, productList);
      }else{
        if(PDPButton){
          var active = typeof productList != 'undefined' && productList != null ? productList.find(item => item.handle === window.wsapps.product_handle) : active;
          if (active) {
            target.setAttribute('aria-label', wsBtnTextWishlist);
            target.closest('#'+wsBtnID).setAttribute('data-categoryId', active.cid);
            target.className = 'ws-active '+wsAddtocartBtnClass;
            target.querySelector('.cs-wishlist-text').textContent = wsBtnTextWishlist;
            target.querySelector('.cs-wishlist-icon').innerHTML = fillicon;
          }else{
            target.setAttribute('aria-label', wsBtnText);
            target.closest('#'+wsBtnID).setAttribute('data-categoryId', '');
            target.classList.remove('ws-active');
            target.querySelector('.cs-wishlist-text').textContent = wsBtnText;
            target.querySelector('.cs-wishlist-icon').innerHTML = icon;
          }
        }else{
          var dataHandle = target.closest(addToWishlistCollection).getAttribute('data-handle');
          var active = typeof productList != 'undefined' && productList != null ? productList.find(item => item.handle === dataHandle) : active;
          if(active){
            target.closest(addToWishlistCollection).setAttribute('data-categoryId', active.cid);
            target.setAttribute('aria-label', wsBtnTextWishlist);
            target.classList.add('ws-active');
            target.querySelector('span').innerHTML = fillicon;
          }else{
            target.setAttribute('aria-label', wsBtnText);
            target.classList.remove('ws-active');
            target.querySelector('span').innerHTML = icon;
          }
        }
      }
    })
    .catch(error => {
      mdvWishFail(error, 'read');
    });
  }


  /* Add Wishlist btn on Collection page */
  function wishlistBtnCollection(productData){  
    //console.log('wishlistBtnCollection ======================================================');
    var csWsIcon = document.querySelectorAll(addToWishlistCollection);
    csWsIcon.forEach(function(element) {
      let buttonInner = element.querySelector('button');
      let span = element.querySelector('span');
      var dataHandle = element.getAttribute('data-handle');
      var matchedProduct = Array.isArray(productData)
        ? productData.find(item => item.handle === dataHandle)
        : null;
      
      if (matchedProduct) {
        element.setAttribute('data-categoryId', matchedProduct.cid);
        element.setAttribute('aria-label', wsBtnTextWishlist);
        if (buttonInner) {
          buttonInner.classList.add('ws-active', 'action_button', 'DishaMavani');
        }        
        if (span) {
          span.innerHTML = fillicon;
        }
      } else {
        if (buttonInner) {
          buttonInner.classList.remove('ws-active', 'action_button', 'DishaMavani');
        }
        if (span) {
          span.innerHTML = icon;
        }
      }
    });    
  }

  /* Add Wishlist btn on PDP page */
  function wishlistBtnPDP(productData) {
    // console.log('wishlistBtnPDP ======================================================');    
    var csWsIconPro = document.querySelectorAll(addToWishlistProduct);
    csWsIconPro.forEach(function(element) {
      let span = element.querySelector('span');
      let spanbutton = element.querySelector('button');
      var matchedProduct = Array.isArray(productData)
        ? productData.find(item => item.handle === window.wsapps.product_handle)
        : null;
      if (matchedProduct) {
        element.setAttribute('data-varId', window.wsapps.variant_id);
        element.setAttribute('data-handle', window.wsapps.product_handle);
        element.setAttribute('data-categoryId', matchedProduct.cid);        
        if (spanbutton) {
          spanbutton.classList.add('ws-active');
        }
        if (span) {
          span.innerHTML = fillicon;
        }
      } else {
        if (spanbutton) {
          spanbutton.classList.remove('ws-active');
        }
        if (span) {
          span.innerHTML = icon;
        }
      }
    });
  }
  async function categoryGetList(target, productHandle, data_id, variant_id, category_id, customer_id, customer_name, customer_email) {
    //console.log('categoryGetList ======================================================  API');
    // mdvWishFlat-1: one wishlist per customer - no list chooser. A tap on an empty star adds straight to the
    // default list (btnClick resolves it); a tap on a filled star removes the product from every list.
    var button = target.querySelector(mdvWsFavBtnSel); // mdvWishWorker-1 (F-C step 2)
    if (button.classList.contains('ws-active')) {
        btnClick(target, productHandle, data_id, variant_id, category_id)
    } else {
        var mdvDrawerPage = document.querySelector('.cart_tab2 #CS-Wishlist-page');
        if (mdvDrawerPage) { mdvDrawerPage.style.display = 'flex'; }
        btnClick(target, productHandle, data_id, variant_id, category_id, null, customer_id, customer_name, customer_email)
    }
  }


  async function btnClick(target, productHandle, data_id, variant_id, category_id, remove, customer_id, customer_name, customer_email) {
    //console.log('btnClick ====================================================== API');
    var button = target.querySelector(mdvWsFavBtnSel); // mdvWishWorker-1 (F-C step 2)
    var mdvTap = { target: target, productHandle: productHandle, data_id: data_id, variant_id: variant_id, category_id: category_id }; // mdvWishWorker-1
    if (button.classList.contains('ws-active')) {
        // mdvWishFlat-1: one wishlist per customer - remove the product from EVERY list that holds it.
        // mdvWishWorker-1: mdvWishT.removeEverywhere sends one product.remove per list holding it, one after another,
        // and stops at the first that is not applied; the result is the server truth afterwards. customer_id, name
        // and email are ignored (the signed proxy identifies the customer). Any failure -> W5 below restores server truth.
        var mdvRmProduct = data_id ? data_id : window.wsapps.product_id;
        var mdvRemoveOk = false; // mdvWishGrid-1 W5
        var mdvRmResults = [];
        var mdvRm = null, mdvRmError = null;
        try {
            mdvRm = await mdvWishT.removeEverywhere(mdvRmProduct);
            mdvRmResults = [mdvRm];
            // mdvWishWorker-1 bugfix: only an APPLIED remove keeps the card hidden. "absent" (nothing was sent) and any
            // other outcome put the card back and redraw it from server truth (W5 below).
            mdvRemoveOk = !!(mdvRm && mdvRm.removed && mdvRm.outcome === 'applied');
        } catch (error) {
            mdvRmError = error;
            mdvRmResults = [error];
        }
        if (mdvRm && mdvRm.superseded) { return; } // a later tap on this product owns the star and the cards
        if (mdvRemoveOk) { mdvWishForget(mdvRmProduct); } else { mdvWishSnap = null; }
        Promise.resolve(mdvRmResults)
            .then(function(result) {
                if (!mdvRemoveOk) { throw new Error('mdvWishFlat-1: the remove failed in at least one list'); }
                console.log("result ===", result);
                if(productHandle){            
                    if(remove){
                        if(listId){
                            let items = $('.page-width #CS-Wishlist-page .cross-sell-product-item').not('.delete_hide');                            
                            if(items.length == 0){                      
                                CSCategoryGetSingle(category_id)
                            }
                            if(document.querySelector('.min-cart').classList.contains('active') && fromUserId){                                
                              CSCategoryGetSingle(category_id);
                            }else{
                              //console.log('6 =================');
                              CSRetrieveWishlistData('drawer');
                            }
                        }else{
                            let cartTabLengths = [];
                            let pageWidthLengths = [];
                            $('.cart_tab2 .productHandlesByCategory').each(function() {
                                let items = $(this).find('.ws-pro-list-image').not('.delete_hide').not('.category_id');              
                                cartTabLengths.push(items.length);
                                if(items.length == 0){ 
                                    $(this).addClass('is-empty');
                                }
                            });
                            
                            if(!fromUserId){
                              $('.page-width .productHandlesByCategory').each(function() {
                                  let items = $(this).find('.ws-pro-list-image').not('.delete_hide').not('.category_id');                                
                                  pageWidthLengths.push(items.length); // Store the length in the array
                                  if (items.length == 0) { 
                                      $(this).addClass('is-empty');
                                      $(this).find('.view_all_link').hide();
                                  }
                              });                              
                              // Compare the lengths after both loops                            
                              for (let i = 0; i < cartTabLengths.length; i++) {
                                  if (cartTabLengths[i] !== pageWidthLengths[i]) {
                                      if(pageWidthLengths[i] == 0 && cartTabLengths[i] > pageWidthLengths[i]){  
                                        //console.log('7 =================');
                                        CSRetrieveWishlistData();                                      
                                      }                                   
                                  }
                              }
                            }
                                                        
                            if(location.pathname.includes(pageUrl) && document.querySelector('.min-cart').classList.contains('active')){
                              //console.log('8 =================');
                              CSRetrieveWishlistData();
                            }else if(location.pathname.includes(pageUrl)){
                              //console.log('9 =================');
                              CSRetrieveWishlistData('drawer');
                            }  
                        }                        
                        if((!document.querySelector('.min-cart').classList.contains('active') && location.pathname.includes('/collections/')) || 
                          (!document.querySelector('.min-cart').classList.contains('active') && location.pathname.includes('/products/'))){
                          //console.log('10 =================');
                          CSRetrieveWishlistData('drawer');
                        }
                    }else{
                        setLoader();
                        CSUpdateUI(button)  
                    }
                }else{              
                    CSUpdateUI(button, 'buttonPDP')
                }
            })
            .catch(function(error) {
                console.log('error', error);
                // mdvWishGrid-1 W5: the remove failed, so restore server truth and the star.
                if (!mdvRemoveOk) {
                    // mdvWishWorker-1 bugfix: the hidden card comes back at once, whether or not the read below succeeds.
                    mdvWishRestoreCard(target);
                    if (button) {
                        button.classList.add('ws-active');
                        var mdvRmIcon = button.querySelector('.cs-wishlist-icon');
                        if (mdvRmIcon) { mdvRmIcon.innerHTML = fillicon; }
                    }
                    var mdvReconcile;
                    mdvWishKeepVisible = true;
                    try {
                        mdvReconcile = CSRetrieveWishlistData(location.pathname.includes(pageUrl) ? undefined : 'drawer');
                    } finally {
                        mdvWishKeepVisible = false;
                    }
                    // mdvWishWorker-1 bugfix: when the server truth already shows the product gone ("absent", or a remove
                    // that was not applied because nothing was left), the redraw is the answer; otherwise the failure UI.
                    var mdvRmGone = !mdvRmError && !!(mdvRm && mdvRm.removed);
                    if (!mdvRmGone) {
                        Promise.resolve(mdvReconcile).then(function () {
                            // mdvWishWorker-1 bugfix 2 (F6): an error after the server applied the remove (e.g. storage lost while
                            // the journal settled): the fresh read shows the product gone, so the redraw is the answer.
                            if (mdvRmError && mdvWishT.held(mdvRmProduct) === false) { return; }
                            mdvWishFail(mdvRmError || mdvWishNotSaved(), 'remove', mdvTap);
                        }); // mdvWishWorker-1
                    }
                }
            });            
    } else {        
        var wsAdd = {};
        // mdvWishFlat-1: every add goes to the customer's default list (lowest list id), never to the clicked
        // or the last list; with no list yet, a FAVOURITE list is created first.
        // mdvWishWorker-1: both happen in mdvWishT.add; no store name, customer id, name or email is sent.
        wsAdd.product_id = data_id ? data_id : window.wsapps.product_id;
        wsAdd.variant_id = variant_id ? variant_id : window.wsapps.variant_id;
        // mdvWishGrid-1 W4: optimistic star + placeholder card BEFORE the write.
        var mdvBtn = target.querySelector(mdvWsFavBtnSel); // mdvWishWorker-1 (F-C step 2)
        var mdvIcon = target.querySelector('.cs-wishlist-icon');
        if (mdvBtn) { mdvBtn.classList.add('ws-active'); }
        if (mdvIcon) { mdvIcon.innerHTML = fillicon; }
        mdvWishPin(wsAdd.product_id); // mdvWishFlat-1: this device's add is shown first (newest-first pin)
        var mdvPending = mdvWishPlaceholder(target, mdvWishDefaultId(mdvWishSnapFor()), wsAdd.product_id, productHandle);
        try {
            var mdvAdd = await mdvWishT.add(wsAdd.product_id, wsAdd.variant_id);
            if (mdvAdd && mdvAdd.superseded) { mdvWishDropPlaceholder(mdvPending); return; } // a later tap owns the star
            if (!mdvAdd || !mdvAdd.saved) { throw mdvWishNotSaved(); } // not applied -> revert
        } catch (error) {
            console.log('error', error);
            mdvWishUnpin(wsAdd.product_id); // mdvWishFlat-1
            mdvWishSnap = null; // mdvWishFlat-1: re-read the wishlist before the next tap
            // mdvWishGrid-1 W4: failure -> drop the placeholder, restore the star, no re-render.
            mdvWishDropPlaceholder(mdvPending);
            if (mdvBtn) { mdvBtn.classList.remove('ws-active'); }
            if (mdvIcon) { mdvIcon.innerHTML = icon; }
            mdvWishFail(error, 'add', mdvTap); // mdvWishWorker-1: 401 -> log in; 429 -> wait; else unavailable
            return;
        }
        try {
            if(category_id){
                $('.cart_tab2 #CS-Wishlist-page').removeClass('selectCategory');
            }
            // W7: '.header .cart a' is the drawer-open trigger (sections/header.liquid), so it stays.
            // W2: the manual append is gone; one full re-render is the only render path. The
            // '.cart_tab_link2' handler re-renders only where it is bound (mdvWishTabRendersDrawer);
            // on product/collection/wishlist pages render here instead, exactly once. The lists stay
            // visible (placeholder included) until the re-render swaps the wrapper.
            mdvWishKeepVisible = true;
            try {
                $('.header .cart a, .cart_tab_link2').trigger('click');
                if (!mdvWishTabRendersDrawer) {
                    CSRetrieveWishlistData('drawer');
                }
            } finally {
                mdvWishKeepVisible = false;
            }
        } catch (error) {
            console.log('error', error);
        }
    }
  }
  
  async function getproHTML(file) {
    try {
      const response = await fetch(file);
      if (!response.ok) {
        // If response is not OK (like 404), return null
        console.warn(`Fetch failed for ${file} — Status: ${response.status}`);
        return null;
      }
      return await response.text();
    } catch (error) {
      console.error(`Fetch error for ${file}:`, error);
      return null;
    }
  }

  // mdvWishGrid-1 W4/W5/W6 helpers. Optimistic DOM only lives until the next full render
  // (CSRetrieveWishlistData replaces the drawer wrapper); no local state is persisted.
  function mdvWishImageSrc(target) {
    var pick = function (img) {
      return img ? (img.currentSrc || img.getAttribute('src') || img.getAttribute('data-src') || '') : '';
    };
    var notIcon = function (img) { return !(img.closest && img.closest('.cs-wishlist-icon, .mdv-ws-fav-btn, #ws-fav-btn')); }; // mdvWishWorker-1 (F-C step 2)
    var src = '';
    if (target && target.id === wsBtnID) {
      var gallery = document.querySelectorAll('.product-page--images-container img, .product-page-mobile--images-container img');
      for (var i = 0; i < gallery.length && !src; i++) {
        if (notIcon(gallery[i])) { src = pick(gallery[i]); }
      }
      if (!src) {
        var og = document.querySelector('meta[property="og:image:secure_url"], meta[property="og:image"]');
        src = og ? (og.getAttribute('content') || '') : '';
      }
    } else {
      for (var el = target ? target.parentElement : null, d = 0; el && el !== document.body && d < 8 && !src; el = el.parentElement, d++) {
        var imgs = el.querySelectorAll('img');
        for (var j = 0; j < imgs.length && !src; j++) {
          if (notIcon(imgs[j])) { src = pick(imgs[j]); }
        }
      }
    }
    return src.indexOf('data:') === 0 ? '' : src;
  }

  function mdvWishPlaceholder(target, categoryId, productId, productHandle) {
    try {
      var row = categoryId ? document.querySelector('.cart_tab2 .productHandlesByCategory-' + categoryId + ' .scroll_product_drawer') : null;
      if (!row) {
        row = document.querySelector('.cart_tab2 .productHandlesByCategory[data-name="FAVOURITE"] .scroll_product_drawer');
      }
      if (!row) { // mdvWishFlat-1: the drawer has a single list block
        row = document.querySelector('.cart_tab2 .productHandlesByCategory .scroll_product_drawer');
      }
      if (!row) { return null; }
      var card = document.createElement('div');
      card.className = 'ws-pro-list-image';
      card.setAttribute('data-mdv-optimistic', '1');
      card.setAttribute('data-id', String(productId || ''));
      var imageWrap = document.createElement('div');
      imageWrap.className = 'cross-sell-product-image';
      var link = document.createElement('a');
      if (productHandle) { link.setAttribute('href', '/products/' + productHandle); }
      var src = mdvWishImageSrc(target);
      if (src) {
        var img = document.createElement('img');
        img.setAttribute('src', src);
        img.setAttribute('alt', '');
        link.appendChild(img);
      }
      imageWrap.appendChild(link);
      card.appendChild(imageWrap);
      var first = row.firstElementChild;
      if (first && first.classList.contains('category_id')) {
        first.insertAdjacentElement('afterend', card);
      } else {
        row.insertBefore(card, row.firstChild);
      }
      var group = row.closest('.productHandlesByCategory');
      var wasEmpty = !!(group && group.classList.contains('is-empty'));
      if (wasEmpty) { group.classList.remove('is-empty'); }
      return { card: card, group: group, wasEmpty: wasEmpty };
    } catch (err) {
      return null;
    }
  }

  function mdvWishDropPlaceholder(pending) {
    if (!pending || !pending.card) { return; }
    if (pending.card.parentNode) { pending.card.parentNode.removeChild(pending.card); }
    if (pending.wasEmpty && pending.group && pending.group.isConnected) { pending.group.classList.add('is-empty'); }
  }

  // W6: true when this product was already rendered in this list (dedupes duplicate server rows).
  function mdvWishSeen(seen, product) {
    var key = product ? product.shopify_product_id : null;
    if (key === undefined || key === null || key === '') { return false; }
    key = String(key);
    if (seen[key]) { return true; }
    seen[key] = 1;
    return false;
  }

  // mdvWishFlat-1: one wishlist per customer (UI-only flatten; the server keeps its lists and memberships).
  //  - snapshot: the last get-wishlist categories read for the signed-in customer (mdvWishSnap);
  //  - default list: the customer's list with the numerically lowest id (normally the original FAVOURITE);
  //  - display (mdvWishFlatten, a pure function of snapshot + pins): this device's own adds first, newest
  //    first (localStorage 'mdvWishAdded' {productId: ms}, pruned to the snapshot); then every list by
  //    descending list id, each newest first; one card per product (the first occurrence wins).
  function mdvWishIsId(id) {
    return (typeof id === 'number' || typeof id === 'string') && /^[1-9][0-9]{0,19}$/.test(String(id));
  }

  function mdvWishIdCmp(a, b) { // numeric order of two decimal ids, compared as strings (safe past 2^53)
    a = String(a); b = String(b);
    if (a.length !== b.length) { return a.length < b.length ? -1 : 1; }
    return a < b ? -1 : (a > b ? 1 : 0);
  }

  function mdvWishDefaultId(categories) {
    var best = null;
    (Array.isArray(categories) ? categories : []).forEach(function (c) {
      if (c && mdvWishIsId(c.id) && (best === null || mdvWishIdCmp(c.id, best) < 0)) { best = c.id; }
    });
    return best;
  }

  function mdvWishFlatten(categories, pins) {
    var lists = (Array.isArray(categories) ? categories : []).filter(function (c) { return c && Array.isArray(c.products); });
    var defaultId = mdvWishDefaultId(lists);
    var category = null;
    lists.forEach(function (c) { if (category === null && defaultId !== null && String(c.id) === String(defaultId)) { category = c; } });
    // mdvWishWorker-1: the Worker snapshot is not in insertion order but has a saved date per item, so the union is
    // ordered by mdvWishT.order (saved date newest first, then local ids) and deduped by Shopify product id.
    var products = mdvWishT.order(lists);
    pins = pins || mdvWishPins();
    var pinOf = function (p) { var v = Number(pins[String(p.shopify_product_id)]); return isFinite(v) && v > 0 ? v : 0; };
    var pinned = products.filter(function (p) { return pinOf(p) > 0; });
    pinned.sort(function (a, b) { return pinOf(b) - pinOf(a); });
    return { category: category, products: pinned.concat(products.filter(function (p) { return pinOf(p) === 0; })) };
  }


  function mdvWishPins() {
    try {
      var raw = window.localStorage.getItem('mdvWishAdded');
      var map = raw ? JSON.parse(raw) : {};
      return map && typeof map === 'object' && !Array.isArray(map) ? map : {};
    } catch (err) {
      return mdvWishPinsMem;
    }
  }

  function mdvWishSavePins(map) {
    mdvWishPinsMem = map;
    try { window.localStorage.setItem('mdvWishAdded', JSON.stringify(map)); } catch (err) {}
  }

  function mdvWishPin(productId) {
    if (productId === undefined || productId === null || productId === '') { return; }
    var map = mdvWishPins();
    map[String(productId)] = Date.now();
    mdvWishSavePins(map);
  }

  function mdvWishUnpin(productId) {
    var map = mdvWishPins();
    if (Object.prototype.hasOwnProperty.call(map, String(productId))) {
      delete map[String(productId)];
      mdvWishSavePins(map);
    }
  }

  function mdvWishRemember(categories) { // mdvWishWorker-1: the snapshot is the proxy-verified customer's; no id
    if (!Array.isArray(categories)) { return; }
    mdvWishSnap = { categories: categories };
    var present = {};
    categories.forEach(function (c) {
      (c && Array.isArray(c.products) ? c.products : []).forEach(function (p) { if (p) { present[String(p.shopify_product_id)] = 1; } });
    });
    var map = mdvWishPins(), now = Date.now(), changed = false;
    Object.keys(map).forEach(function (k) {
      // prune to the snapshot; an add still in flight keeps its pin for 30 s
      if (!present[k] && !(now - Number(map[k]) < 30000)) { delete map[k]; changed = true; }
    });
    if (changed) { mdvWishSavePins(map); }
  }

  function mdvWishSnapFor() {
    return mdvWishSnap ? mdvWishSnap.categories : null;
  }


  function mdvWishIdsHolding(categories, productId) {
    var ids = [];
    (Array.isArray(categories) ? categories : []).forEach(function (c) {
      if (!c || !mdvWishIsId(c.id) || !Array.isArray(c.products)) { return; }
      var holds = c.products.some(function (p) { return p && String(p.shopify_product_id) === String(productId); });
      if (holds && ids.indexOf(c.id) === -1) { ids.push(c.id); }
    });
    return ids;
  }

  // mdvWishWorker-1: the default-list, first-save (FAVOURITE) and remove-everywhere list resolution now lives in
  // mdvWishT (one serial queue, server truth after every write). The helpers below render its outcome.
  function mdvWishPageOk(element) { // on a retired shared-list link the page container keeps its message
    return !(fromUserId && element && element.closest && element.closest('.main-wrapper'));
  }

  function mdvWishStatus(key) {
    var text = mdvWishT.label(key);
    document.querySelectorAll('.CS-Wishlist-page').forEach(function (element) {
      if (!mdvWishPageOk(element)) { return; }
      element.querySelectorAll('.mdv-wish-status').forEach(function (node) { node.remove(); });
      if (!text) { return; }
      var box = document.createElement('div');
      box.className = 'mdv-wish-status';
      box.setAttribute('role', 'status');
      box.setAttribute('aria-live', 'polite');
      box.textContent = text;
      element.insertBefore(box, element.firstChild);
    });
  }

  function mdvWishSharedUnavailable() {
    document.querySelectorAll('.main-wrapper .CS-Wishlist-page').forEach(function (element) {
      element.innerHTML = '';
      var box = document.createElement('div');
      box.className = 'mdv-wish-status mdv-wish-shared';
      box.setAttribute('role', 'status');
      box.textContent = mdvWishT.label('shared_unavailable');
      element.appendChild(box);
    });
  }

  function mdvWishClearRendered() { // I4: nothing of a previous session stays on screen
    document.querySelectorAll('.CS-Wishlist-page').forEach(function (element) { if (mdvWishPageOk(element)) { element.innerHTML = ''; } });
    document.querySelectorAll('[data-mdv-optimistic]').forEach(function (node) { node.remove(); });
    mdvWishSnap = null;
    if (location.pathname.includes('/products/')) { wishlistBtnPDP(); }
    wishlistBtnCollection();
  }

  function mdvWishLogin(target, productHandle, data_id, variant_id, category_id) { // as the signed-out tap
    if(window.wsapps.customerLogin){
      window.location.href = window.wsapps.urlLogin;
    } else {
      var wishlistPopup = document.querySelector('body > .wishlist-popup');
      if (wishlistPopup) {
        wishlistPopup.classList.add('active');
        loginPopupFunction(target, productHandle, data_id, variant_id, category_id)
        registerPopupFunction(target, productHandle, data_id, variant_id, category_id)
      }
    }
  }

  function mdvWishNotSaved() { var error = new Error('mdvWishT not applied'); error.mdvWish = 'unavailable'; return error; }

  // mdvWishWorker-1 bugfix (11 Oct 2026): x on a wishlist page or drawer card hides the card before the write. Remember
  // where it was, so any outcome other than an applied remove puts it back at once - without waiting for a server read,
  // which can fail too (e.g. a lost IndexedDB connection on iOS Safari).
  var mdvWishHidden = typeof WeakMap === 'function' ? new WeakMap() : null;

  function mdvWishHideCard(target) {
    if (!mdvWishHidden || !target || !target.closest) { return; }
    var card = target.closest('.ws-pro-list-image');
    mdvWishHidden.set(target, { card: card, parent: card ? card.parentNode : null, next: card ? card.nextSibling : null,
      item: target.closest('.cross-sell-product-item') });
  }

  function mdvWishRestoreCard(target) {
    var hidden = mdvWishHidden && target ? mdvWishHidden.get(target) : null;
    if (!hidden) { return false; }
    mdvWishHidden.delete(target);
    if (hidden.item) { hidden.item.classList.remove('delete_hide'); }
    if (hidden.card && !hidden.card.isConnected && hidden.parent && hidden.parent.isConnected) {
      hidden.parent.insertBefore(hidden.card, hidden.next && hidden.next.parentNode === hidden.parent ? hidden.next : null);
    }
    return true;
  }

  // mdvWishWorker-1 bugfix 2 (F5): on a product page the only status container is the cart drawer, which the theme keeps
  // invisible while it is closed. A failed tap on the PDP star also shows the message right below the star's row; the
  // next tap on the star removes it.
  function mdvWishStarStatus(target, key) {
    document.querySelectorAll('.mdv-wish-star-status').forEach(function (node) { node.remove(); });
    var text = mdvWishT.label(key);
    if (!text || !target || target.id !== 'ws-fav-html' || !target.isConnected) { return; }
    var row = (target.closest && target.closest('.add-to-cart-wishlist')) || target;
    var box = document.createElement('div');
    box.className = 'mdv-wish-status mdv-wish-star-status';
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    box.style.cssText = 'width:100%;order:1;margin:6px 0 0;';
    box.textContent = text;
    row.insertAdjacentElement('afterend', box);
    target.addEventListener('click', function () { box.remove(); }, { once: true });
  }

  function mdvWishFail(error, context, tap) { // 401 -> log in; 429 -> wait; anything else -> unavailable
    var code = error && error.mdvWish ? error.mdvWish : 'unavailable';
    console.warn(mdvWishT.marker, context, code);
    if (tap) { mdvWishStarStatus(tap.target, code === 'auth' ? 'login_required' : (code === 'rate' ? 'rate_limited' : 'unavailable')); }
    if (code === 'auth') {
      if (tap) { mdvWishLogin(tap.target, tap.productHandle, tap.data_id, tap.variant_id, tap.category_id); }
      mdvWishStatus('login_required');
      return code;
    }
    mdvWishStatus(code === 'rate' ? 'rate_limited' : 'unavailable');
    return code;
  }


  function mdvWishForget(productId) { // after a successful remove-from-all
    mdvWishUnpin(productId);
    if (!mdvWishSnap) { return; }
    mdvWishSnap = { customer: mdvWishSnap.customer, categories: mdvWishSnap.categories.map(function (c) {
      if (!c || !Array.isArray(c.products)) { return c; }
      return Object.assign({}, c, { products: c.products.filter(function (p) { return !p || String(p.shopify_product_id) !== String(productId); }) });
    }) };
  }

  function btnClickAfterPopup(target, productHandle, data_id, variant_id, categary_id, customer_id, customer_name, customer_email) {
    //console.log('btnClickAfterPopup ====================================================== API');
    // mdvWishWorker-1: after the popup registration a fresh bootstrap identifies the new customer (nothing from the
    // form is sent); mdvWishT.add creates FAVOURITE first when there is no list. The page reloads after a save, as before.
    mdvWishT.invalidate();
    mdvWishT.add(data_id ? data_id : window.wsapps.product_id, variant_id ? variant_id : window.wsapps.variant_id)
      .then(function (result) {
        if (result && result.saved) { location.reload(true); }
        else { mdvWishFail(mdvWishNotSaved(), 'add'); }
      })
      .catch(function (error) {
        console.log('error', error);
        mdvWishFail(error, 'add');
      });
  }




  function CSWSPageDataHeader(isNot) {
    // console.log('CSWSPageDataHeader ======================================================');
    var customerId = window.wsapps && ajaxcustomer_id ? `data-cusID="${ajaxcustomer_id}"` : `onclick="location.href = '/account';"`;
    var CSWSPageDataHeader = `
    <div id="cs-page-header">
      <div class="csws-header">
        <div class="csws-header-title">
          <div class="button_back"><a href="/"></a><h4 class="csws-header-uname">`+ajaxcustomer_name+` Wishlist</h4></div>
        </div>
      </div>
      `;
    
    CSWSPageDataHeader += `</div>`;
    return CSWSPageDataHeader;
  }
  function emptyhtmlGrid(params) {
    //console.log('emptyhtmlGrid ======================================================');    
    var emptyhtml = `<div class="cs-wishlist-wrapper"><div class="productHandlesByCategory is-empty" data-name="FAVOURITE">`;
    // mdvWishFlat-1: no list name, list menu (Rename) or delete prompt in the empty state.
    emptyhtml += `<ul class="${window.wsapps.parentClass}"></ul>`;
    emptyhtml += `</div></div>`;
    return emptyhtml;
  }

  function CSWSPageDataInnerHeader(key, id, new_create, hasProduct, isNot, element){   
    // console.log('CSWSPageDataInnerHeader ====================================================== Disha 2', key);    
    var cs_cate_title = ''
    if(key){
      cs_cate_title = key;
    }else{
      cs_cate_title = '<input class="cs-new-list-title" id="cs-new-list-title" type="text" placeholder="Enter List Name"  required="required" maxlength="40">'      
      var createListButtons = document.querySelectorAll("#csws-create-list");
      if (createListButtons.length > 0) {
        createListButtons.forEach(function(button) {
          button.setAttribute('disabled', 'disabled');
        });
      }
    }
    var CSWSPageDataInnerHeaderInfo = `<div class="cs-list-header-title"><h4>`+cs_cate_title.toLowerCase()+`</h4>
      <div class="cs-dropdown cs-dropdown-options 0000">
          <button class="cs-dropdown-toggle" id="cs-dropdown-toggle">
              `+window.wsapps.dots+`
          </button>
          <ul class="cs-dropdown-menu-main" role="menu">
              ${new_create === 'new_create' || new_create === 'empty_create' && !isNot ? `` : `<li class="cs-dropdown-menu-action">
                <button class="cs-dropdown-menu-btn category-edit" aria-label="Rename" data-val="${id}">Rename</button>
            </li>`}
              ${isNot ? `` : `<li class="cs-dropdown-menu">
                  <button class="cs-dropdown-menu-btn category-destroy" aria-label="Delete" data-val="${id}">Delete</button>
              </li>`}
          </ul>
        </div>
      </div>`;
    if(new_create){      
      if(element){
        var csWishlistWrappers = document.querySelectorAll('.cart_tab2 .cs-wishlist-wrapper');  
      }else{
        var csWishlistWrappers = document.querySelectorAll('.main-wrapper .cs-wishlist-wrapper');  
      }
      csWishlistWrappers.forEach(function(csWishlistWrapper) {
        var newDiv = document.createElement('div');
        newDiv.className = 'productHandlesByCategory ';
        newDiv.setAttribute('data-name', 'newCategory');
        newDiv.innerHTML = CSWSPageDataInnerHeaderInfo + `<ul class="${window.wsapps.parentClass}"></ul>` + removeContent();
        csWishlistWrapper.insertAdjacentElement('afterbegin', newDiv);
      });      
    }
    return CSWSPageDataInnerHeaderInfo;
  }


  function removeContent() {
    //console.log('removeContent ======================================================');
    var removeContent = `<div class="cs-list-delete-modal" style="display: none;">
              <div class="cs-delete-title">Delete this list?</div>
              <div class="cs-delete-btn">
                  <button class="cs-btn-cancel">Cancel</button>
                  <button class="cs-btn-delete">Delete</button>
              </div>
          </div>`;
    return removeContent;
  }

  
  // Set Page Wishlist
  function CSWishlistPageData(result, productList){
    //console.log('CSWishlistPageData ====================================================== Disha 1');
    if (fromUserId) { mdvWishSharedUnavailable(); return; } // mdvWishWorker-1
    var wishlists = result.data.categories;
    if (Array.isArray(wishlists) && wishlists.length){      
      var html = CSWSPageDataHeader(true);
    }else{
      var html = CSWSPageDataHeader();
      html += emptyhtmlGrid();
    }
    // $('.cs-wishlist-wrapper').remove();
    if(wishlists.length > 0) {
      // mdvWishFlat-1: one wishlist - ONE block (the default list's id and name, so the existing handlers still
      // resolve a list) holding every list's products, newest first, one card per product. No list name, list
      // menu, View All, delete prompt, Add list, or dragging between lists ('dropzone').
      var mdvFlatPage = mdvWishFlatten(wishlists);
      html += `<div class="cs-wishlist-wrapper">`;
      [mdvFlatPage.category || wishlists[0]].forEach(category => {
        html += `<div class="productHandlesByCategory productHandlesByCategory-${category.id} category-id-${category.category_name} ${mdvFlatPage.products.length == 0 ? 'is-empty' : ''}" data-name="${category.category_name}" id="category-id-${category.category_name.replace(/\s/g, '')}" data-cat-id="${category.id}">`;
        html += `<div class="${window.wsapps.parentClass}">`;
        // Create category title
        const title = document.createElement('h2');
        title.textContent = category.category_name;
        // Create a list for products
        var mdvSeenPage = {}; // mdvWishGrid-1 W3 (no 6-item cap) + W6 (one card per product per list)
        mdvFlatPage.products.forEach(product => {
          if (mdvWishSeen(mdvSeenPage, product)) { return; }
          // mdvWishIds-1: the card star also carries class mdv-ws-fav-btn (id ws-fav-btn unchanged)
          html += `
          <div draggable="true" data-pro-id="${product.shopify_product_id}" data-varId="${product.variants.shopify_variant_id}" class="ws-pro-list-image main-product-item" data-handle="${product.handle}">
            <div class="main-product-wrp">
              <a href="/pages/${window.wsapps.pageURL}?list_id=${category.id}" data-href="${product.image_url}" class="main-product-inner" draggable="false">
                <img src="${product.image_url}" alt="${product.title}" loading="lazy" data-pro-name="${product.title}" />
              </a>
              <div class="cs_ws_icon" data-id="${product.shopify_product_id}" data-handle="${product.handle}" data-varId="${product.shopify_product_id}">
                <button aria-label="Add to Wishlist" id="ws-fav-btn" type="button" class="action_button  ws-active mdv-ws-fav-btn"><span class="cs-wishlist-icon"><svg width="25" height="21" viewBox="0 0 23 21" fill="none" xmlns="https://www.w3.org/2000/svg"><path d="M11.5801 0.992188L14.668 7.2511L21.5801 8.2606L16.5801 13.13L17.7678 20.0183L11.5801 16.7642L5.39243 20.0183L6.58008 13.13L1.58008 8.2606L8.49219 7.2511L11.5801 0.992188Z" stroke="#333333" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg></span></button>
              </div>
            </div>  
          </div>`
        });
        html += `</div>`;
        html += `</div>`;
      });
      html += `</div>`;
    }
    html += `<div class="sign-out link_btn"><a href='/account/logout'>Log out</a></div>`;
    var wishlistHTML = document.querySelectorAll('.main-wrapper .CS-Wishlist-page');
    if(wishlistHTML.length > 0){
      for (var i = 0; i < wishlistHTML.length; i++) {
        wishlistHTML[i].classList.add("wishlist-overflow");
        wishlistHTML[i].innerHTML = '';  
        wishlistHTML[i].innerHTML += html;
      }
    }
  } 

  // Set Page Drawer Data
  function CSWishlistDrawerData(result, productList){   
    //console.log('CSWishlistDrawerData ====================================================== Disha 2');
    var wishlists = result.data.categories;
    if (Array.isArray(wishlists) && wishlists.length){      
      var html = CSWSPageDataHeader(true);
    }else{
      
      var html = CSWSPageDataHeader();
      html += emptyhtmlGrid();
    }
    
    // $('.cs-wishlist-wrapper').remove();
    if(wishlists.length > 0) {
      // mdvWishFlat-1: one wishlist - ONE block (the default list's id and name) holding every list's products,
      // newest first, one card per product. No list name, list menu, chooser tile, View All, delete prompt or
      // Add list.
      var mdvFlatDrawer = mdvWishFlatten(wishlists);
      html += `<div class="cs-wishlist-wrapper">`;
      [mdvFlatDrawer.category || wishlists[0]].forEach(category => {
        html += `<div class="productHandlesByCategory productHandlesByCategory-${category.id} category-id-${category.category_name} ${mdvFlatDrawer.products.length == 0 ? 'is-empty' : ''}" data-name="${category.category_name}" id="category-id-${category.category_name.replace(/\s/g, '')}" data-cat-id="${category.id}">`;
        html += `<div class="${window.wsapps.parentClass}">`;
        // Create category title
        const title = document.createElement('h2');
        title.textContent = category.category_name;
        
        // Create a list for products
        var mdvSeenDrawer = {}; // mdvWishGrid-1 W6: one card per product per list
        mdvFlatDrawer.products.forEach((product, index) => {
          if (mdvWishSeen(mdvSeenDrawer, product)) { return; }
          // mdvWishIds-1: the card star also carries class mdv-ws-fav-btn (id ws-fav-btn unchanged)
          html += `
          <div class="ws-pro-list-image ${product.handle}" data-loop="${index}">
            <div class="cross-sell-product-item-page cross-sell-product-item cross-sell-item main-product-item two_column_item" data-handle="${product.handle}" data-id="${product.shopify_product_id}">
              <div class="cross-sell-product-image">
                <a href="/products/${product.handle}">
                  <img src="${product.image_url}" alt="${product.title}" loading="lazy">
                </a>
              </div>
              <div class="cross-sell-product-content">
                <div class="cross-sell-product-price">      
                  <div class="cs-product-title">
                    <a href="/products/${product.handle}">${product.title}</a>
                  </div>
                  <div class="cs_ws_icon" data-id="${product.shopify_product_id}" data-handle="${product.handle}" data-varId="${product.shopify_product_id}">
                    <button aria-label="Add to Wishlist" id="ws-fav-btn" type="button" class="action_button  ws-active mdv-ws-fav-btn"><span class="cs-wishlist-icon"><svg width="25" height="21" viewBox="0 0 23 21" fill="none" xmlns="https://www.w3.org/2000/svg"><path d="M11.5801 0.992188L14.668 7.2511L21.5801 8.2606L16.5801 13.13L17.7678 20.0183L11.5801 16.7642L5.39243 20.0183L6.58008 13.13L1.58008 8.2606L8.49219 7.2511L11.5801 0.992188Z" stroke="#333333" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg></span></button>
                  </div>                  
                </div>
              </div>
            </div>
          </div>`
        });
        html += `</div>`;
        html += `</div>`;
      });
      html += `</div>`;
    }
    html += `<div class="sign-out link_btn"><a href='/account/logout'>Log out</a></div>`;
    
    var wishlistHTML = document.querySelectorAll('.CS-drawer-wishlist');
    $('.productHandlesByCategory[data-name="newCategory"]:not(.is-empty)').remove();
    if(wishlistHTML.length > 0){
      for (var i = 0; i < wishlistHTML.length; i++) {
        wishlistHTML[i].classList.add("wishlist-overflow");
        wishlistHTML[i].innerHTML = '';  
        wishlistHTML[i].innerHTML += html;
      }
    }  
    if(location.pathname.includes('/collections/')){ 
      wishlistBtnCollection(productList); 
    }
    if(location.pathname.includes('/products/')){ 
      wishlistBtnPDP(productList);
    }
    formData();
  } 

  async function formData() {
    //console.log('formData ======================================================')
    document.querySelectorAll('.cross-sell-product-item').forEach(async (item) => {      
      var productHandle = item.getAttribute('data-handle');
      if(productHandle){
        var addpendHTML = await getproHTML(`/products/${productHandle}?view=ws-pro-list-new`);

        if (!addpendHTML) {
          item.style.display = 'none';
          document.querySelector(`.min-cart-inner .ws-pro-list-image .cross-sell-product-item[data-handle="${productHandle}"]`)?.parentElement?.style.setProperty("display", "none");
          document.querySelector(`.ws-pro-list-image.main-product-item[data-handle="${productHandle}"]`)?.remove();
          document.querySelector(`.ws-pro-list-image.${productHandle}`)?.style.setProperty('display', 'none');
          return;
        }

        if (addpendHTML.includes('fetch-template')) {
          if (!addpendHTML.trim() || addpendHTML.includes('Page Not Found')) {
            item.style.display = 'none';
            document.querySelector(`.ws-pro-list-image.main-product-item[data-handle="${productHandle}"]`)?.remove();
            document.querySelector(`.ws-pro-list-image.${productHandle}`)?.style.setProperty('display', 'none');
            return;
          }else {
            item.querySelector('.mdv-product-add')?.remove(); // mdvWishWorker-1 (F-C step 2)
            item.querySelector('.cross-sell-product-price.fetch-template')?.remove();
            item.querySelector('.cross-sell-product-price')?.insertAdjacentHTML('afterend', addpendHTML);
          }
        }
      }
    });
  }
  
  document.addEventListener("click", function(e){ 
    document.querySelectorAll('.cs-dropdown-menu-main, .cs-dropdown-menu-mai').forEach((item) => {
      item.classList.remove('active');  
    })
    // Show toggle
    var csDropdownToggle = e.target.closest('.cs-dropdown-toggle'); // Or any other selector.    
    if(csDropdownToggle){
      var dropdownMenu = csDropdownToggle.parentElement.querySelector('.cs-dropdown-menu-main');
      if (dropdownMenu) {
        dropdownMenu.classList.toggle('active');
      }
    }




    // Hide productHandlesByCategory content
    var listHeaderDropdowns  = e.target.closest('.list-header-dropdown'); // Or any other selector.    
    if (listHeaderDropdowns) {
      var closestProductHandles = listHeaderDropdowns.closest('.productHandlesByCategory');
      if (closestProductHandles) {
        closestProductHandles.classList.toggle('productHandlesByCategory_hide');
      }
    }
      


    // Add in Wishlist Collection and Product page
    var target = e.target.closest('#'+wsBtnID) || e.target.closest(addToWishlistCollection) ; // Or any other selector.    
    if (target) {
      var abcParentDiv = target.closest('.CS-Wishlist-page');
      var productHandle = target.getAttribute('data-handle'); 
      var data_id = target.getAttribute('data-id'); 
      var variant_id = target.getAttribute('data-varId'); 
      var category_id = target.getAttribute('data-categoryId');
      var parentElement = target.parentElement;
      var productElement = target.closest('.productHandlesByCategory');      
      if (productElement &&  productElement.getAttribute('data-cat-id')) {
        category_id = productElement.getAttribute('data-cat-id');        
      }
      if(ajaxcustomer_id){
        if (abcParentDiv) { 
          mdvWishHideCard(target); // mdvWishWorker-1 bugfix: W5 puts the card back unless the remove is applied
          target.closest('.cross-sell-product-item') ? e.target.closest('.cross-sell-product-item').classList.add("delete_hide") : '';
          target.closest('.ws-pro-list-image') ? e.target.closest('.ws-pro-list-image').remove() : '';
          btnClick(target, productHandle, data_id, variant_id, category_id, 'remove');
        }else{
          if (target.querySelector(mdvWsFavBtnSel).classList.contains('ws-active')) { // mdvWishWorker-1 (F-C step 2)
            btnClick(target, productHandle, data_id, variant_id, category_id, 'remove');
            target.querySelector(mdvWsFavBtnSel).classList.remove('ws-active');
            target.querySelector('.cs-wishlist-icon').innerHTML = icon;
          } else {
            categoryGetList(target, productHandle, data_id, variant_id, category_id)
          }
        }
      }else{
        if(window.wsapps.customerLogin){
          window.location.href = window.wsapps.urlLogin;
        } else {
          var wishlistPopup = document.querySelector('body > .wishlist-popup');
          if (wishlistPopup) {
            wishlistPopup.classList.add('active');     
            loginPopupFunction(target, productHandle, data_id, variant_id, category_id)
            registerPopupFunction(target, productHandle, data_id, variant_id, category_id)
            
          }
        }
      }
    }
  });



  // mdvWishWorker-1: the old list create/rename/delete/move functions are not ported (F-B do-not-port list).


  // Drag functionality
  (function() {
    var dragged = null, positions = [];
    function getObject(parentId, html, index, elementID){
       let item = {
        parentId: parentId,
        html: html,
        position: index,
        currentID: elementID
      };
      return item;
    }
    document.addEventListener("dragstart", (event) => {
      return dragged = event.target;
    });

    document.addEventListener("dragend", (event) => {        
    });

    document.addEventListener("dragover", function(event) {
      return event.preventDefault();
    });

  }).call(this);


  // Login form submission
  var formSubmitting = false;
  function loginPopupFunction(target, productHandle, data_id, variant_id, category_id) {
    //console.log('loginPopupFunction ======================================================');
    $('body > .wishlist-popup #main-login-drawer.wishlist-popup-form > #customer_login').off('submit').on("submit", function(e) {      
      $('div#main-login-drawer .error_info').remove();
      e.preventDefault();      
      if (!formSubmitting) {          
        $.ajax({
          url: '/account/login',
          method: 'post',
          data: $(this).serialize(),
          dataType: 'html',
          async: true,
        }).done(function(response){
          if(response.indexOf('Incorrect email or password.') !== -1){
            $('div#main-login-drawer').prepend('<p class="error_info text-unset">Incorrect Email or Password.</p>');   
            formSubmitting = false;
          }else{
            formSubmitting = true;
            if(variant_id){          
              var tempDiv = document.createElement('div');
              tempDiv.innerHTML = response;
              var customer_id = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customerId');
              ajaxcustomer_id = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customerId');
              
              var customer_name = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customerName');
              ajaxcustomer_name = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customerName');
              
              var customer_email = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customeremailId');
              ajaxcustomer_email = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customeremailId');

              console.log('11 =================');
              mdvWishT.invalidate(); // mdvWishWorker-1: a fresh bootstrap identifies the customer who just logged in
              CSRetrieveWishlistData('drawer', customer_id, customer_name)
              categoryGetList(target, productHandle, data_id, variant_id, category_id, customer_id, customer_name, customer_email)              
            }else{
              location.reload(true);
            }
            $('.wishlist-popup').removeClass('active');    
          }
        });
      }
    });
  }


  $('.cart_tab2 > .wishlist-popup #main-login-drawer.wishlist-popup-form > #customer_login, body.template-main-cs-wishlist .main-wrapper #tab1 > #customer_login').on( "submit", function( e ) {    
    $('div#main-login-drawer .error_info').remove();    
    e.preventDefault();    
    $.ajax({
      url: '/account/login',
      method: 'post',
      data: $(this).serialize(),
      dataType: 'html',
      async: true,
    }).done(function(response){
      $('.error_info').remove();
      if(response.indexOf('Incorrect email or password.') !== -1){
        $('div#main-login-drawer, body.template-main-cs-wishlist .main-wrapper #tab1').prepend('<p class="error_info text-unset">Incorrect Email or Password.</p>');       
      }else{
        var tempDiv = document.createElement('div');
        tempDiv.innerHTML = response;
        var customer_id = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customerId');
        ajaxcustomer_id = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customerId');
        ajaxcustomer_name = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customerName');
        ajaxcustomer_email = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customeremailId');
        //console.log('12 =================');
        mdvWishT.invalidate(); // mdvWishWorker-1: a fresh bootstrap identifies the customer who just logged in
        CSRetrieveWishlistData('drawer', customer_id);
        document.querySelector('.wishlist-popup').classList.remove('active');
        document.querySelector('.cart_tab2 #CS-Wishlist-page').style.display = 'flex';
        if(location.pathname.includes(pageUrl)){
          location.reload(true);
        }
      }
    });
  });


  // Registration form submission
  var registerFormSubmitting = false;
  function registerPopupFunction(target, productHandle, data_id, variant_id, category_id) {
    //console.log('registerPopupFunction ======================================================');    
    $('body > .wishlist-popup #register_form.wishlist-popup-form > #create_customer_model').on( "submit", function( e ) {
      $('div#register_form .error_info').remove();
      e.preventDefault();  
       if (!registerFormSubmitting) { 
        $.ajax({
          url: '/account',
          method: 'post',
          data: $(this).serialize(),
          dataType: 'html',
          async: true,
        }).done(function(response){  
          registerFormSubmitting = false;
          if(response.indexOf('Email is invalid') !== -1 || response.indexOf('Email can&#39;t be blank') !== -1 || response.indexOf('Password can&#39;t be blank.') !== -1 ){      
            $('div#register_form').prepend('<ul class="error_info"><li>Password can&#39;t be blank.</li><li>Email can&#39;t be blank.</li></ul>');    
          }else if(response.indexOf('Email is invalid') !== -1 ){      
            $('div#register_form').prepend('<p class="error_info text-unset">Incorrect Email or Password.</p>');       
          } else if(response.indexOf('This email address is already associated with an account. If this account is yours, you can') !== -1){
            $('div#register_form').prepend('<p class="error_info">This email address is already associated with an account. If this account is yours, you can <a href="/account/login#recover">reset your password</a></p>');       
          } else{
            registerFormSubmitting = true;
            if(variant_id){          
              var tempDiv = document.createElement('div');
              tempDiv.innerHTML = response;
              var customer_id = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customerId');
              var customer_name = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customerName');
              var customer_email = tempDiv.querySelector('.wishlist-popup.login').getAttribute('data-customeremailId');
              
              btnClickAfterPopup(target, productHandle, data_id, variant_id, category_id, customer_id, customer_name, customer_email);
            }else{
              location.reload(true);
            }
            $('.wishlist-popup').removeClass('active');    
          }
        });
      }
    });
  }
  
  //registerPopupFunction()
  $('.cart_tab2 > .wishlist-popup #register_form.wishlist-popup-form > #create_customer_model, body.template-main-cs-wishlist .main-wrapper #tab2 > #create_customer_model').on( "submit", function( e ) {
      $('div#register_form .error_info').remove();
      e.preventDefault();    
      $.ajax({
        url: '/account',
        method: 'post',
        data: $(this).serialize(),
        dataType: 'html',
        async: true,
      }).done(function(response){    
        //console.log('response =', response)
        $('.error_info').remove();
        if(response.indexOf('Email is invalid') !== -1 || response.indexOf('Email can&#39;t be blank') !== -1 || response.indexOf('Password can&#39;t be blank.') !== -1 ){      
          $('div#register_form, body.template-main-cs-wishlist .main-wrapper #tab2').prepend('<ul class="error_info"><li>Password can&#39;t be blank.</li><li>Email can&#39;t be blank.</li></ul>');    
        }else if(response.indexOf('Email is invalid') !== -1 ){      
          $('div#register_form, body.template-main-cs-wishlist .main-wrapper #tab2').prepend('<p class="error_info text-unset">Incorrect Email or Password.</p>');       
        } else if(response.indexOf('This email address is already associated with an account. If this account is yours, you can') !== -1){
          $('div#register_form, body.template-main-cs-wishlist .main-wrapper #tab2').prepend('<p class="error_info">This email address is already associated with an account. If this account is yours, you can <a href="/account/login#recover">reset your password</a></p>');       
        } else{
          
          location.reload(true);
          
          $('.wishlist-popup').removeClass('active');    
        }
      });
  });
    
  
}
/// Disha Hetre



/* --------------- Register button Click Start For Popup ---------------*/
function registerClick(registerLink, mainpopup) {
  if (registerLink) {
    registerLink.addEventListener('click', function() {      
      //console.log('registerClick ======================================================');
      var checkGenderSetValue = $('body > .wishlist-popup ul.checkGender-option li.activeFinal').text();
      $('.checkGender').find('.checkGender-value').val(checkGenderSetValue);
      if(mainpopup){
        var loginPopup = document.querySelector('body > .wishlist-popup.login');
        var registerPopup = document.querySelector('body > .wishlist-popup.register');
      }else{
        var loginPopup = document.querySelector('.wishlist-popup.login');
        var registerPopup = document.querySelector('.wishlist-popup.register');
      }
      if (loginPopup) {
        loginPopup.classList.remove('active');
      }
      if (registerPopup) {
        registerPopup.classList.add('active');
      }
    });
  } 
}
var registerLink1 = document.querySelector('.popup-register-link.register-popup#instance1');
var registerLink2 = document.querySelector('.popup-register-link.register-popup#instance2');
if(registerLink1){ registerClick(registerLink1, 'mainpopup') } 
if(registerLink2){ registerClick(registerLink2) }
/* --------------- Register button Click End For Popup ---------------*/

/* --------------- Login button Click Start For Popup ---------------*/
function loginClick(loginLink, mainpopup) { 
  if (loginLink) {
    loginLink.addEventListener('click', function() {
      //console.log('Login Click ======================================================');
      if(mainpopup){
        var loginPopup = document.querySelector('body > .wishlist-popup.login');
        var registerPopup = document.querySelector('body > .wishlist-popup.register');
      }else{
        var loginPopup = document.querySelector('.wishlist-popup.login');
        var registerPopup = document.querySelector('.wishlist-popup.register');
      }
      if (loginPopup) {
        loginPopup.classList.add('active');
      }
      if (registerPopup) {
        registerPopup.classList.remove('active');
      }
    });
  }       
}
var loginLink1 = document.querySelector('.popup-register-link.login-popup#instance1');
var loginLink2 = document.querySelector('.popup-register-link.login-popup#instance2');
if(loginLink1){ loginClick(loginLink1, 'mainpopup') } 
if(loginLink2){ loginClick(loginLink2) }
/* --------------- Login button Click End For Popup ---------------*/


function closePopup() {
  //console.log('closePopup ======================================================');
  $('body > .wishlist-popup').removeClass('active');  
  $('.wishlist-popup-form').find('form').find("input[type=email], input[type=text], input[type=password], textarea").val("");
}
$('body').on('click','.wishlist-popup-close', function(e){
  //console.log('wishlist-popup-close ======================================================');
  e.preventDefault();
  closePopup();
});
$(document).keyup(function(e) {
  if (e.keyCode == 27) { 
    closePopup();
  }
});
window.onclick = function(e) {
  if ($(e.target).hasClass('main-overlay')) {
    // console.log('closePopup ======================================================');
    closePopup();
  }
}



function CSWishlistrefreshData(drawer){  
  //console.log('CSWishlistrefreshData ======================================================');
  if(window.wsapps.wishlist_status == '1') {
    if(ajaxcustomer_id){
      if(drawer){
        //console.log('13 =================');
        CSRetrieveWishlistData('drawer');  
      }else{
        if(!(location.pathname.includes(pageUrl))){
          const element = document.querySelector('.cart_tab_link2');          
          if (location.pathname.includes('/collections/') || element && element.classList.contains('active')) {
            //console.log('14 =================');
            CSRetrieveWishlistData();
          }
        }
      }
    }else{
      if(location.pathname.includes('/products/')){ 
        wishlistBtnPDP()
      }
      if(location.pathname.includes('/collections/')){ 
        wishlistBtnCollection()
      }
      emptyPage();
    }
  }
}
$(document).ready(function(){

    $(document).on("click","#CS-Wishlist-page .cross-sell-product-item .select-option",function(e) {
      e.preventDefault();
      $('.check-stock-msg ').removeClass('active');
      $('#CS-Wishlist-page .cross-sell-product-item .variant-listbox').not($(this).parents('.variant-listbox')).removeClass("active");
      $(this).parents('.variant-listbox').toggleClass("active");
      $('#CS-Wishlist-page .cross-sell-product-item .cart-recommended-option-ul').not($(this).parents('.cross-sell-product-item').find('.cart-recommended-option-ul')).slideUp();
      if($(this).parents('.variant-listbox').hasClass("active")){
        $(this).parents('.cross-sell-product-item').find('.cart-recommended-option-ul').slideDown();
        // $(".min-cart-top").animate({ scrollTop: $('.min-cart-top').height() + $('.min-cart-main').height()}, 500);
      }else{
        $(this).parents('.cross-sell-product-item').find('.cart-recommended-option-ul').slideUp();
      }
    });

  $(document).on("click","#CS-Wishlist-page .cross-sell-product-item .mdv-variant-listbox ul li.variant-size",function(e) { // mdvWishWorker-1 (F-C step 2)
    e.preventDefault();
    e.stopPropagation();
    $(this).parents('.variant-listbox').removeClass("active");
    var current_variant = $(this).attr("value");
    $(this).parents("#CS-Wishlist-page .cross-sell-product-item").find("select.mdv-variant-listbox").val(current_variant).change(); // mdvWishWorker-1 (F-C step 2)
    $(this).parents("#CS-Wishlist-page .cross-sell-product-item").find(".cross-sell-product-btn input").trigger("click");
    $('.cart_tab_link1').trigger('click')
  });

  $(document).on('click', function (event) {
    if (!$(event.target).closest('.variant-listbox').length) {
      $('#CS-Wishlist-page .cross-sell-product-item .variant-listbox').removeClass("active");
    }
  });
});


$(document).ready(function(){
   $('body').on('click','.cart_tab2 .productHandlesByCategory .scroll_product_drawer > div > a', function(e){
    e.preventDefault();
    var catId = $(this).attr('data-catId');    
    CSCategoryGetSingle(catId)
  })
})


